// Hand-authored content for each Dallas pillar page.
// Sourced from Phase2_20Cities_Website_Plan.xlsx (Dallas tab) keyword clusters
// and standard industry knowledge. No invented rates, APRs, reviews, or stats.

const AGENCY_NAME = "Compass Lenders";

export type PillarContent = {
  /** 2 short paragraphs explaining the product in plain English. */
  whatItIs: string[];
  /** Business types this fits best (4-6 short items). */
  bestForBusinesses: string[];
  /** Situations / triggers (3-5 short items). */
  situations: string[];
  /** Override the "Program at a glance" facts. */
  terms?: {
    amount?: string;
    term?: string;
    speed?: string;
    qualifications?: string;
    documentation?: string;
  };
  /** 4-6 real-world use cases. */
  commonUses: { h: string; p: string }[];
  /** Slug-specific FAQ pairs that REPLACE the generic Excel questions. */
  faq: { q: string; a: string }[];
};

const A = "Final rates and terms depend on the lender and the borrower's profile.";
const N = "no upfront fees, soft credit pull, and side-by-side offers from multiple lenders.";

export const PILLAR_CONTENT: Record<string, PillarContent> = {
  /* ===================== MONEY PILLARS ===================== */

  "sba-loans": {
    whatItIs: [
      "SBA loans are loans made by a bank or non-bank lender and partially guaranteed by the U.S. Small Business Administration. The guarantee lets lenders extend longer terms, larger amounts, and lower payments than they would offer on a conventional loan to the same borrower.",
      "For Dallas businesses, the two most common programs are the SBA 7(a) (flexible-use working capital, acquisition, or refinance) and the SBA 504, used for owner-occupied real estate and large equipment. We package the file, route it to an SBA Preferred Lender, and handle the back-and-forth so you can focus on the business.",
    ],
    bestForBusinesses: [
      "Established small businesses with 2+ years of operating history",
      "Owners buying real estate for their own operation",
      "Buyers acquiring an existing business or franchise",
      "Operators refinancing high-cost short-term debt",
      "Healthcare, manufacturing, and professional service firms",
    ],
    situations: [
      "You want the longest possible term and the lowest monthly payment",
      "You can wait 30-60 days for funding in exchange for better terms",
      "You're financing an SBA-eligible use (real estate, equipment, acquisition, working capital, refinance)",
      "You have collateral, decent personal credit, and a clean business",
    ],
    terms: {
      amount: "Up to $5M",
      term: "10-25 years (real estate) · 7-10 years (working capital)",
      speed: "30-60 days to fund",
      qualifications:
        "2+ years in business, U.S. citizen/LPR owner, no recent defaults on federal debt",
      documentation:
        "Business + personal tax returns (3 yrs), debt schedule, P&L, business plan for use of funds",
    },
    commonUses: [
      { h: "Buy owner-occupied real estate", p: "Acquire the building your business operates out of with up to 90% financing under the SBA 504 program." },
      { h: "Acquire an existing business", p: "Finance up to 90% of the purchase price plus working capital in a single 7(a) loan." },
      { h: "Refinance expensive debt", p: "Consolidate MCAs, short-term advances, or higher-rate term loans into one longer-term SBA payment." },
      { h: "Long-term working capital", p: "Fund expansion, hiring, or new market entry over a 7-10 year amortization." },
      { h: "Buy heavy equipment", p: "Finance large, long-life equipment under the 504 program with terms that match the asset's useful life." },
    ],
    faq: [
      { q: "How long does an SBA loan take to fund in Dallas", a: `Most SBA 7(a) files we place in Dallas fund in 30-60 days from a complete application. Express programs can fund faster on smaller amounts. ${A}` },
      { q: "What credit score do I need for an SBA loan", a: `Most SBA lenders look for a FICO of roughly 680 or higher on owner guarantors, but the full file (cash flow, collateral, time in business) matters more than the score alone. ${A}` },
      { q: "Do I have to put money down for an SBA loan", a: "Yes. SBA programs generally expect a 10-20% borrower equity injection on acquisitions and real estate. Working-capital 7(a) loans typically require less or none." },
      { q: "Can I use an SBA loan to refinance other business debt", a: "Yes. Refinancing higher-cost short-term debt into longer-term SBA payments is one of the most common 7(a) use cases for Dallas borrowers." },
      { q: "Do you charge anything to apply for an SBA loan", a: `No. ${N} We're paid by the lender at closing.` },
    ],
  },

  "sba-7a-loan": {
    whatItIs: [
      "The SBA 7(a) is the SBA's flagship loan program. Banks and non-bank lenders make the loan; the SBA guarantees a portion, which lets the lender offer up to $5M with terms as long as 25 years.",
      "It's the most flexible SBA product. You can use it for working capital, business acquisition, partner buyout, equipment, owner-occupied real estate, or to refinance qualifying business debt. We help Dallas borrowers package the file and route it to an SBA Preferred Lender so the SBA can delegate the credit decision and shorten the timeline.",
    ],
    bestForBusinesses: [
      "Established Dallas businesses with 2+ years of consistent revenue",
      "Buyers acquiring a business, franchise, or partner's stake",
      "Service, professional, and light-industrial firms",
      "Owners refinancing stacked short-term debt",
    ],
    situations: [
      "You need a larger amount or a longer term than a conventional bank will offer",
      "You're acquiring a business and need both purchase financing and working capital",
      "You can wait ~30-60 days for funding in exchange for better economics",
      "You have decent credit and a real use of funds you can document",
    ],
    terms: {
      amount: "Up to $5M",
      term: "Up to 10 years (working capital) · up to 25 years (real estate)",
      speed: "30-60 days from complete file",
      qualifications:
        "2+ years operating, U.S. citizen/LPR owner, demonstrable cash flow to service the new debt",
      documentation:
        "Business + personal tax returns, interim financials, debt schedule, purchase docs for acquisitions",
    },
    commonUses: [
      { h: "Business acquisition", p: "Finance up to 90% of the purchase plus rolled-in working capital on a single note." },
      { h: "Partner buyout", p: "Buy out a co-owner under SBA guidelines and consolidate ownership." },
      { h: "Working capital + expansion", p: "Long-term capital for hiring, marketing, and new locations." },
      { h: "Refinance high-cost debt", p: "Reset short-term advances into one 7-10 year SBA payment." },
      { h: "Owner-occupied real estate", p: "Buy a building you'll operate out of, often with stronger terms than conventional CRE." },
    ],
    faq: [
      { q: "What's the maximum SBA 7(a) loan amount", a: `The SBA 7(a) program caps at $5M. ${A}` },
      { q: "How long are SBA 7(a) terms", a: "Real estate up to 25 years; equipment up to 10 years (or asset life); working capital and acquisitions typically 7-10 years." },
      { q: "Do I need collateral for an SBA 7(a) loan", a: "The SBA requires the lender to take available business collateral and a personal guarantee from owners with 20%+ equity. The SBA generally won't decline solely for lack of collateral if the deal otherwise cash-flows." },
      { q: "How is an SBA 7(a) different from an SBA 504", a: "7(a) is general-purpose and can be used for working capital, acquisition, or real estate. 504 is structured specifically for owner-occupied real estate and large equipment, with a CDC + bank co-funding structure." },
      { q: "Can I use SBA 7(a) to buy a franchise", a: "Yes, if the franchise is on the SBA Franchise Directory. We confirm eligibility before submission." },
    ],
  },

  "business-line-of-credit": {
    whatItIs: [
      "A business line of credit is a revolving credit facility your business can draw against, repay, and draw against again, similar to a credit card but typically with lower rates and higher limits. You only pay interest on the balance you've drawn, not the full limit.",
      "Lines are issued by banks (lower rates, slower approval, tougher qualification) and by online lenders (higher rates, faster approval, lighter docs). For Dallas borrowers we'll quote both lanes so you can see the tradeoff between speed and cost on your specific file.",
    ],
    bestForBusinesses: [
      "Businesses with seasonal or lumpy revenue",
      "B2B service firms waiting on customer payments",
      "Contractors and trades with project-based cash needs",
      "Established small businesses wanting a standby reserve",
      "Owners who want flexibility instead of a fixed lump sum",
    ],
    situations: [
      "You need a financial cushion for surprises, not a one-time lump sum",
      "Your revenue dips in certain months and you need to float payroll",
      "You're bidding on projects and need to show working-capital reserves",
      "You want to stop using high-interest credit cards for short-term needs",
    ],
    terms: {
      amount: "$10K - $500K (online) · up to several million (bank)",
      term: "Revolving · typical 6-24 month draw periods, renewable",
      speed: "Online: 1-3 business days · Bank: 2-6 weeks",
      qualifications:
        "6+ months operating, $10K+ in monthly deposits, no recent serious derogatories",
      documentation:
        "3 months business bank statements; banks also require tax returns and financials",
    },
    commonUses: [
      { h: "Cover a payroll gap", p: "Draw enough to bridge a slow receivable week and pay it back when the customer wires." },
      { h: "Buy inventory at a discount", p: "Take advantage of vendor early-pay terms or bulk pricing." },
      { h: "Emergency repairs", p: "Replace a failed compressor, HVAC, or vehicle without scrambling for emergency-rate financing." },
      { h: "Bid on larger contracts", p: "Show working capital reserves on RFPs and proposals." },
      { h: "Marketing pushes", p: "Fund a seasonal ad campaign and repay from the resulting revenue." },
    ],
    faq: [
      { q: "How is a line of credit different from a term loan", a: "A term loan is a one-time lump sum with a fixed repayment schedule. A line of credit is revolving: you draw, repay, and reuse the limit. Pay interest only on what's drawn." },
      { q: "How fast can I get a business line of credit", a: `Online lenders can approve in 24 hours and fund in 1-3 business days. Bank lines take 2-6 weeks. ${A}` },
      { q: "Will applying for a line of credit hurt my credit", a: "Initial pre-qualification is a soft pull with no impact. A hard pull happens only if you accept an offer and move to underwriting." },
      { q: "Can a startup get a line of credit", a: "Most lines require at least 6 months in business and steady deposits. For brand-new startups we usually steer you to a startup-loan product or a personal-credit-backed line." },
      { q: "Do I have to use the whole line", a: "No. You only pay interest on what you draw. The unused portion sits available with no carrying cost from most online lenders." },
    ],
  },

  "working-capital-loans": {
    whatItIs: [
      "Working capital loans are short-to-mid-term financing used to cover day-to-day operating costs (payroll, rent, inventory, utilities) rather than to buy a long-life asset. The loan is typically unsecured or secured by general business assets, and is repaid from operating cash flow.",
      "For Dallas businesses, working capital is the right product when you have a clear short-term need (cover a payroll cycle, restock for a busy season, take on a new contract) and want predictable payments rather than a revolving line.",
    ],
    bestForBusinesses: [
      "Established businesses with steady monthly deposits",
      "Owners managing a seasonal or lumpy revenue cycle",
      "Service businesses without significant fixed-asset collateral",
      "Companies that just landed a contract and need ramp-up capital",
    ],
    situations: [
      "You need a defined sum to cover a known short-term gap",
      "You want fixed payments, not a revolving balance",
      "You can't or don't want to wait 30+ days for SBA",
      "You'd rather not pledge specific equipment or real estate",
    ],
    terms: {
      amount: "$10K - $500K",
      term: "3 months - 5 years",
      speed: "Often funded in 24-72 hours",
      qualifications: "6+ months operating, ~$10K+ monthly deposits, no active bankruptcy",
      documentation: "One-page application + 3 months of business bank statements",
    },
    commonUses: [
      { h: "Bridge a payroll cycle", p: "Cover wages while waiting on a customer payment or a slow A/R week." },
      { h: "Pre-buy inventory for a busy season", p: "Stock up ahead of demand to capture better unit pricing and avoid stockouts." },
      { h: "Fund a new contract", p: "Cover the upfront materials and labor required to accept a larger job." },
      { h: "Cover slow-month rent and utilities", p: "Smooth out a known dip without dipping into reserves." },
      { h: "Marketing or growth pushes", p: "Run a campaign now and repay from the additional revenue it produces." },
    ],
    faq: [
      { q: "What can I use a working capital loan for", a: "Any short-term operating expense: payroll, rent, inventory, utilities, marketing, supplies. Lenders don't require receipts. They care about your ability to repay." },
      { q: "How much working capital can I qualify for", a: `Typical approvals run 1×-1.5× of your average monthly business deposits, capped at $500K for most online programs. ${A}` },
      { q: "Is it secured or unsecured", a: "Most working capital loans under $250K are unsecured at the asset level, with a personal guarantee from owners. Larger amounts may require a UCC blanket lien." },
      { q: "How fast can a working capital loan fund", a: "Same-day to 72 hours for online lenders; 1-3 weeks for bank programs. We quote both lanes." },
      { q: "Do you require tax returns for working capital", a: "Generally no for files under $250K. Business bank statements drive most decisions." },
    ],
  },

  "small-business-loans": {
    whatItIs: [
      "\"Small business loan\" is the umbrella term for financing extended to a small business, typically a fixed-amount term loan repaid in equal installments. The right structure depends on your use of funds, time in business, and how fast you need to close.",
      "For Dallas owners, we'll match you to the program that actually fits: SBA when you have time and want the best terms, a conventional term loan for established companies, a line of credit for ongoing flexibility, or a short-term loan when speed matters more than rate.",
    ],
    bestForBusinesses: [
      "Established Dallas-area small businesses (6+ months operating)",
      "Owners ready to grow but not ready for the SBA paperwork burden",
      "Service, retail, and trades businesses with steady revenue",
      "Companies wanting one fixed monthly payment, not a draw line",
    ],
    situations: [
      "You have a defined use of funds and want a clear payoff date",
      "You want one application that gets quoted by multiple lenders",
      "Your bank declined or stalled and you need a real answer",
      "You want to compare SBA, conventional, and short-term side-by-side",
    ],
    terms: {
      amount: "$25K - $500K (typical) · up to $5M with SBA",
      term: "6 months - 25 years depending on program",
      speed: "24 hours - 60 days depending on program",
      qualifications: "6+ months in business, $10K+ monthly deposits, U.S. business entity",
      documentation: "1-page application + 3 months bank statements (more for larger files)",
    },
    commonUses: [
      { h: "Hire and onboard new staff", p: "Fund recruiting, salaries, and ramp-up while the new hires build their book." },
      { h: "Open a second location", p: "Cover build-out, deposits, and opening payroll until the location reaches steady revenue." },
      { h: "Buy equipment or vehicles", p: "Pair a term loan with the right depreciation strategy for the asset." },
      { h: "Build inventory ahead of a launch", p: "Pre-fund a product launch, seasonal push, or new sales channel." },
      { h: "Marketing and brand investment", p: "Fund a campaign that pays back over 6-12 months." },
    ],
    faq: [
      { q: "What's the easiest business loan to qualify for", a: "Short-term revenue-based products approve on bank deposits alone, often with a 550+ FICO. Easiest doesn't mean cheapest; we'll show the full picture." },
      { q: "Can I get a business loan with bad credit", a: `Yes. Revenue-based products, MCAs, and some equipment financing approve on cash flow rather than personal credit. ${A}` },
      { q: "How much business loan can I qualify for", a: "A common rule of thumb is 1×-1.5× monthly business deposits for short-term products; up to 10× annual cash flow for SBA. The right ceiling depends on your file." },
      { q: "Do I have to give a personal guarantee", a: "Almost every small business loan in the U.S. requires a personal guarantee from owners holding 20%+ of the business." },
      { q: "How long do business loans take to fund", a: "From same-day (online short-term) to 60+ days (SBA). The right answer depends on which program fits." },
    ],
  },

  "short-term-business-loans": {
    whatItIs: [
      "Short-term business loans are fixed-amount loans with repayment windows of roughly 3 to 18 months. They're designed for situations where you need capital quickly and can pay it back from near-term revenue.",
      "Because the term is short and underwriting is fast, pricing is higher than a traditional bank loan. The speed and lighter documentation still make them the right tool for time-sensitive opportunities or short revenue gaps for Dallas businesses.",
    ],
    bestForBusinesses: [
      "Businesses with strong, steady recent revenue",
      "Owners chasing a time-sensitive opportunity",
      "Service and trades businesses with quick A/R turnover",
      "Companies bridging to a larger, longer-term loan",
    ],
    situations: [
      "You need money this week, not next month",
      "You expect higher revenue in the next few months to repay it",
      "You don't have the time or paperwork bandwidth for SBA",
      "You want a defined payoff date, not a revolving balance",
    ],
    terms: {
      amount: "$5K - $250K",
      term: "3 - 18 months",
      speed: "24-72 hours",
      qualifications: "6+ months operating, ~$10K+ monthly deposits",
      documentation: "1-page application + 3 months business bank statements",
    },
    commonUses: [
      { h: "Cover a one-time large bill", p: "Tax payment, insurance premium, or vendor catch-up." },
      { h: "Pre-buy inventory for a season", p: "Stock up before peak revenue months and pay back from the lift." },
      { h: "Fund a quick project", p: "Take on a contract that needs upfront materials and labor." },
      { h: "Bridge to a longer-term loan", p: "Cover the gap until SBA or bank financing closes." },
    ],
    faq: [
      { q: "How short can a short-term business loan be", a: "Most run 3 to 18 months. A few products offer 60-90 day bridge structures for very specific use cases." },
      { q: "Are short-term loans more expensive than long-term loans", a: `Yes. Faster funding and lighter underwriting come with higher pricing, typically a higher factor rate or APR. ${A}` },
      { q: "How fast can a short-term loan fund", a: "Often within 24-72 hours from a complete file." },
      { q: "Will a short-term loan show up on my personal credit", a: "Most online lenders report only to business credit bureaus. Personal credit is pulled to qualify but isn't typically reported month-to-month." },
      { q: "Can I pay a short-term loan off early", a: "Many short-term loans have prepayment discounts or no prepayment penalty. We'll flag the prepayment terms on every offer." },
    ],
  },

  "merchant-cash-advance": {
    whatItIs: [
      "A merchant cash advance (MCA) is not a loan. It's a purchase of a portion of your future receivables. The funder advances a lump sum today and is repaid by holding back a percentage of your daily card or bank deposits until the agreed amount is delivered.",
      "MCAs are the fastest-funding product in business finance and approve on revenue rather than credit. That makes them powerful for retail, restaurant, and service operators with steady volume, though the all-in cost is higher than conventional financing, so they should be used deliberately.",
    ],
    bestForBusinesses: [
      "Restaurants, retail, and personal-care businesses with daily card sales",
      "Owners with bruised personal credit but strong revenue",
      "Businesses needing money in 24-48 hours",
      "Operators bridging to a longer, cheaper loan",
    ],
    situations: [
      "Speed matters more than rate",
      "Your credit doesn't qualify for a conventional loan but your revenue is strong",
      "You have a specific short-term opportunity that will repay quickly",
      "You've already been declined by banks and SBA",
    ],
    terms: {
      amount: "$5K - $500K",
      term: "Repaid via 3-18 months of daily holdback",
      speed: "Same-day to 48 hours",
      qualifications: "3+ months in business, ~$8K+ monthly deposits, U.S. business",
      documentation: "Application + 3-4 months of business bank/processor statements",
    },
    commonUses: [
      { h: "Emergency equipment repair", p: "Get a critical piece of equipment back online without losing days of revenue." },
      { h: "Quick inventory restock", p: "Replace a sold-through inventory line before missing sales." },
      { h: "Cover an unexpected tax or insurance bill", p: "Smooth a one-time large expense over daily revenue." },
      { h: "Stack-out / pay off other advances", p: "Consolidate multiple existing MCAs into a single, longer position." },
    ],
    faq: [
      { q: "Is a merchant cash advance a loan", a: "Legally no. It's a purchase of future receivables, not a loan. There is no APR; the cost is expressed as a factor rate (e.g., 1.30 means $1.30 is owed for every $1.00 advanced)." },
      { q: "How is an MCA repaid", a: "Through a daily ACH from your business bank account, or by holding back a percentage of card sales, until the full purchased amount is delivered." },
      { q: "Can I get an MCA with bad credit", a: `Yes. MCA approvals are driven by your monthly revenue, not your FICO. Owners in the 500s regularly qualify if deposits are strong. ${A}` },
      { q: "Are MCAs expensive", a: "All-in cost is higher than a bank loan or SBA. They make sense when speed, credit, or time-in-business rules out cheaper options." },
      { q: "Can I get out of an MCA early", a: "Some funders offer a small discount for early payoff; others charge the full purchased amount regardless. We confirm the prepayment policy on every offer." },
    ],
  },

  "unsecured-business-loans": {
    whatItIs: [
      "An unsecured business loan doesn't require you to pledge specific collateral (real estate, equipment, vehicles) to back the loan. Approval is based on your business cash flow and personal credit; owners still sign a personal guarantee.",
      "Unsecured structures are common for service businesses and asset-light operators who simply don't have machinery or real estate to pledge. The tradeoff is typically a shorter term and higher rate than a secured loan of the same size.",
    ],
    bestForBusinesses: [
      "Service businesses (consultants, agencies, professional services)",
      "Asset-light operators (SaaS, ecommerce, online services)",
      "Owners who don't want to lien personal real estate",
      "Companies needing speed and lighter documentation",
    ],
    situations: [
      "You have no significant fixed-asset collateral to pledge",
      "You don't want a UCC against your equipment or property",
      "You want a faster decision than a secured bank loan",
      "Your business has steady deposits and decent owner credit",
    ],
    terms: {
      amount: "$10K - $250K",
      term: "6 months - 5 years",
      speed: "1-3 business days",
      qualifications: "6+ months operating, ~$10K+ monthly deposits, 600+ FICO common",
      documentation: "1-page application + 3 months of business bank statements",
    },
    commonUses: [
      { h: "Cover operating expenses", p: "Payroll, rent, utilities, and supplies during a slow stretch." },
      { h: "Fund marketing or growth", p: "Run campaigns or hire reps without pledging assets." },
      { h: "Refinance high-cost balances", p: "Roll up expensive short-term debt without a collateral filing." },
      { h: "Bridge to long-term financing", p: "Cover the gap while you wait on an SBA or bank close." },
    ],
    faq: [
      { q: "What does unsecured really mean", a: "The lender doesn't take a specific asset as collateral. Owners still sign a personal guarantee, and the lender may file a UCC-1 on general business assets." },
      { q: "Is an unsecured loan harder to qualify for", a: "Usually yes. Without collateral, lenders weight cash flow and credit more heavily. The trade-off is no specific asset on the line." },
      { q: "Are unsecured business loans more expensive", a: `Typically yes, since the lender has less recovery if things go wrong. ${A}` },
      { q: "Will the lender file a UCC against my business", a: "Many unsecured lenders file a UCC-1 on general business assets. It's not a lien on a specific item, but it does show up on business credit reports." },
      { q: "What credit score is needed for an unsecured business loan", a: "A 600+ FICO opens most online programs; 680+ opens better-priced ones. Strong revenue can offset weaker credit." },
    ],
  },

  "startup-business-loans": {
    whatItIs: [
      "Startup business loans are financing programs designed for companies with less than two years of operating history. Most traditional lenders won't quote a startup at all, so the realistic options narrow to SBA startup loans, microloans, equipment financing (which secures itself with the asset), business credit cards, and personal-credit-backed lines.",
      "For Dallas startups, we focus on what's actually fundable today, typically a combination of an SBA startup loan or microloan, plus a credit line backed by the owner's personal credit, plus targeted equipment financing if a long-life asset is involved.",
    ],
    bestForBusinesses: [
      "Newly formed businesses (under 2 years)",
      "Owners with strong personal credit (700+)",
      "First-time founders with a documented plan",
      "Franchise startups on the SBA Franchise Directory",
      "Industries with clear, defensible unit economics",
    ],
    situations: [
      "You've been turned down by your bank because of time in business",
      "You have personal credit you can leverage temporarily",
      "You're buying an existing business (treated more favorably than greenfield)",
      "You have a clear use of funds with documented projections",
    ],
    terms: {
      amount: "$5K - $150K (typical) · up to $5M for SBA-backed acquisitions",
      term: "1 - 10 years depending on program",
      speed: "1 week (credit-line based) to 60 days (SBA)",
      qualifications: "Strong owner credit, documented business plan, owner equity injection",
      documentation: "Business plan, projections, personal financials, owner tax returns",
    },
    commonUses: [
      { h: "Initial inventory and equipment", p: "Stock the shelves and tool the shop before opening day." },
      { h: "Build-out and lease deposits", p: "Cover landlord deposits, contractor draws, and signage." },
      { h: "Working capital runway", p: "Fund payroll and overhead until revenue stabilizes." },
      { h: "Franchise fees and brand requirements", p: "Pay the franchise fee, branded equipment, and required marketing." },
      { h: "Acquiring an existing business", p: "Buy a turnkey operation instead of building from zero." },
    ],
    faq: [
      { q: "Can I get a business loan with no revenue yet", a: "Yes, but the realistic options narrow to SBA startup loans, microloans, equipment financing, and personal-credit-backed lines. Owner credit, equity injection, and a documented plan do most of the work." },
      { q: "How much can a startup actually borrow", a: `Most no-revenue startups can responsibly access $25K-$150K. Acquisitions of an existing cash-flowing business can support much larger amounts. ${A}` },
      { q: "Do I need a business plan to apply", a: "For SBA and bank programs, yes, a written plan with projections is required. For credit-line products it's usually not." },
      { q: "Will my personal credit get pulled", a: "Yes. For startups, personal credit and an owner equity injection are the two biggest decision drivers." },
      { q: "Should I form an LLC before applying", a: "Yes. Lenders fund registered business entities, not personal names. We can guide on entity setup before submission." },
    ],
  },

  "business-acquisition-loans": {
    whatItIs: [
      "A business acquisition loan finances the purchase of an existing business: a competitor, a turnkey operation, a partner's stake, or a franchise unit. Acquisitions are typically funded under the SBA 7(a) program, which can finance up to 90% of the purchase plus working capital in a single loan.",
      "For Dallas-area acquisitions we structure the file around the target's cash flow (debt service coverage), the buyer's equity injection, and any seller financing, then route to lenders actively writing acquisitions in that industry.",
    ],
    bestForBusinesses: [
      "Strategic buyers acquiring competitors",
      "Operators buying out a co-owner or partner",
      "Search-fund and individual buyers stepping into ownership",
      "Multi-unit franchisees adding another location",
      "Family-business successors taking over from a retiring owner",
    ],
    situations: [
      "The target business has 2+ years of documentable cash flow",
      "You have at least 10% equity to inject (cash or rolled equity)",
      "You can demonstrate operational experience or transferable skills",
      "The deal has clean books and a verifiable purchase agreement",
    ],
    terms: {
      amount: "$100K - $5M (SBA 7a) · larger amounts conventional",
      term: "10 years (working capital portion) · up to 25 yrs (with real estate)",
      speed: "45-90 days to close",
      qualifications: "10%+ buyer equity injection, target shows DSCR ≥ 1.15, clean books",
      documentation: "LOI/purchase agreement, 3 yrs target tax returns + financials, buyer resume",
    },
    commonUses: [
      { h: "Buy a competitor", p: "Consolidate market share and combine operations." },
      { h: "Partner buyout", p: "Acquire a co-owner's stake and consolidate ownership." },
      { h: "Acquire a turnkey operation", p: "Step into an established cash-flowing business instead of starting cold." },
      { h: "Franchise resale", p: "Buy an existing unit from a retiring franchisee." },
      { h: "Family business succession", p: "Finance a planned generational handoff." },
    ],
    faq: [
      { q: "How much do I need to put down on a business acquisition loan", a: `SBA generally requires at least 10% buyer equity, of which at least half must be the buyer's own cash. The rest can be seller financing on standby. ${A}` },
      { q: "Can I roll real estate into the acquisition loan", a: "Yes. If the target operates out of owner-occupied real estate, the SBA 7(a) can finance the business and the building on a single, longer-term note." },
      { q: "What is DSCR and why does it matter", a: "Debt Service Coverage Ratio compares the target's cash flow to the new loan payment. SBA lenders look for DSCR of roughly 1.15× or better on the post-close pro-forma." },
      { q: "Do I need industry experience to get approved", a: "Strongly preferred but not always required. Transferable management experience plus a strong operations plan can overcome industry inexperience for the right deal." },
      { q: "How long does a business acquisition loan take to close", a: "Typically 45-90 days from accepted LOI to funding, depending on diligence and SBA processing." },
    ],
  },

  "accounts-receivable-financing": {
    whatItIs: [
      "Accounts receivable (A/R) financing is a revolving line of credit secured by your unpaid invoices. The lender advances a percentage of eligible receivables, typically up to 85%, and as your customers pay, the line resets and you can borrow again.",
      "Unlike factoring, A/R financing keeps the invoices in your name. Your customers don't know a lender is involved; you continue to bill and collect. It's the right structure for B2B businesses with long payment terms that want flexibility without selling their receivables.",
    ],
    bestForBusinesses: [
      "B2B companies that invoice on Net-30/60/90 terms",
      "Staffing, manufacturing, distribution, and wholesale firms",
      "Owners who want to keep customer relationships in their own name",
      "Businesses needing more borrowing capacity than a term loan provides",
    ],
    situations: [
      "Your cash is locked up in long-dated invoices",
      "You don't want customers to know a lender is involved",
      "You want a line that grows with your sales",
      "Your credit profile is decent but you need more capacity",
    ],
    terms: {
      amount: "Up to 85% of eligible A/R · typical lines $100K - $10M",
      term: "Revolving; 12-month renewable agreements",
      speed: "1-3 weeks to set up · same-day funding once live",
      qualifications: "B2B customers, organized A/R aging, $50K+ in eligible receivables",
      documentation: "A/R aging report, customer list, sample invoices, business financials",
    },
    commonUses: [
      { h: "Smooth long payment cycles", p: "Get paid this week instead of in 60 days while keeping the invoice in your name." },
      { h: "Fund payroll on staffing files", p: "Cover weekly payroll while billing customers on monthly cycles." },
      { h: "Take on a larger customer", p: "Accept a contract with longer terms without choking your cash." },
      { h: "Buy inventory for a contract", p: "Fund materials for orders you'll invoice on Net-60 terms." },
    ],
    faq: [
      { q: "How is A/R financing different from invoice factoring", a: "A/R financing is a loan secured by invoices. You keep the invoices, you keep the customer relationship, and you handle collections. Factoring sells the invoices to a third party who then collects from your customer." },
      { q: "What percentage of my invoices can I borrow against", a: `Typically up to 85% of eligible A/R, with the balance released (less fees) when the customer pays. ${A}` },
      { q: "Will my customers know I'm using A/R financing", a: "No. The invoices stay in your name. Some lenders may file a UCC, but that doesn't appear on customer-facing communication." },
      { q: "What makes an invoice eligible", a: "Typically: U.S. commercial customer, no offset disputes, under 90 days old, and not concentrated more than ~25% with a single customer." },
      { q: "What does A/R financing cost", a: "Pricing is usually a draw fee plus an ongoing interest rate on the balance, which is meaningfully cheaper than factoring for most borrowers." },
    ],
  },

  "invoice-factoring": {
    whatItIs: [
      "Invoice factoring is the sale of your outstanding invoices to a third party (the factor) at a discount. The factor advances most of the invoice value to you today, typically 80-95%, and then collects directly from your customer when the invoice comes due.",
      "Factoring is the fastest way to convert B2B invoices into cash, and approval is driven by the credit quality of your customers, not by you. That makes it especially common in trucking, staffing, and wholesale, where customers are large and credit-worthy.",
    ],
    bestForBusinesses: [
      "Trucking and freight carriers",
      "Staffing agencies",
      "Manufacturers and wholesalers selling to retailers",
      "B2B service firms with strong creditworthy customers",
      "Owners who'd rather outsource collections",
    ],
    situations: [
      "You bill creditworthy B2B customers and can't wait 30-90 days",
      "Your own credit isn't strong but your customers' is",
      "You're comfortable having the factor collect from customers",
      "You want capacity that grows automatically with sales",
    ],
    terms: {
      amount: "Up to 90% advance on invoice face value",
      term: "Per-invoice; 12-month service agreement common",
      speed: "Same-day funding once you're set up",
      qualifications: "B2B invoices to creditworthy customers, U.S.-based, no major disputes",
      documentation: "A/R aging, sample invoices, customer credit info, business setup docs",
    },
    commonUses: [
      { h: "Trucking and freight factoring", p: "Get paid on the load instead of waiting on the broker or shipper." },
      { h: "Fund staffing payroll", p: "Pay your contractors weekly while invoicing clients monthly." },
      { h: "Cover material costs on new orders", p: "Buy supplies for orders you'll invoice and factor as they ship." },
      { h: "Outsource collections", p: "Let the factor chase late payments while you focus on operations." },
    ],
    faq: [
      { q: "How does invoice factoring work step by step", a: "You issue an invoice. The factor advances most of the face value (80-95%). The factor collects from your customer. When the customer pays, you receive the reserve balance less the factor fee." },
      { q: "Will my customers know I'm factoring", a: "Usually yes. The factor typically sends a Notice of Assignment and collects payments directly. \"Non-notification\" factoring exists but is rarer and stricter." },
      { q: "How much does factoring cost", a: `Discount fees typically run 1-4% per 30 days the invoice is outstanding. Pricing depends on customer credit, volume, and dispute risk. ${A}` },
      { q: "What is recourse vs non-recourse factoring", a: "Recourse: if your customer doesn't pay, you buy the invoice back. Non-recourse: the factor absorbs the credit loss (but charges more). Most small-business factoring is recourse." },
      { q: "Can I factor only some of my invoices", a: "Many factors offer \"spot\" factoring on a per-invoice basis. Others require all invoices from selected customers. We'll match you to the right structure." },
    ],
  },

  "revenue-based-financing": {
    whatItIs: [
      "Revenue-based financing (RBF) provides a lump sum of capital that you repay as a fixed percentage of monthly revenue, until a pre-agreed total amount has been delivered. Payments flex with your sales: you pay more in strong months and less when revenue dips.",
      "RBF and asset-based lending are popular with subscription, ecommerce, and SaaS businesses in Dallas because they let owners raise growth capital without giving up equity and without the rigid fixed-payment structure of a term loan.",
    ],
    bestForBusinesses: [
      "SaaS and subscription businesses with predictable MRR",
      "Ecommerce and DTC brands with platform sales data",
      "Service businesses with recurring contracts",
      "Asset-light operators that aren't ideal for traditional bank loans",
    ],
    situations: [
      "Your revenue is steady but lumpy month to month",
      "You'd rather not raise equity at this stage",
      "You can't pledge significant fixed-asset collateral",
      "You want repayment that flexes with sales",
    ],
    terms: {
      amount: "$10K - $1M (online RBF) · much larger via asset-based lending",
      term: "Repaid as % of revenue until target cap is delivered (typically 6-36 months)",
      speed: "2-5 business days",
      qualifications: "$10K+ monthly recurring or platform revenue, 6+ months operating",
      documentation: "Platform connection (Stripe, Shopify, Amazon) or 6 months bank statements",
    },
    commonUses: [
      { h: "Scale paid acquisition", p: "Fund ad spend now and repay as the resulting revenue arrives." },
      { h: "Build inventory for ecommerce", p: "Pre-buy SKUs ahead of a launch or seasonal push." },
      { h: "Hire ahead of growth", p: "Bring on engineers or AEs before revenue catches up." },
      { h: "Refinance MCA debt", p: "Convert harsh daily-payment advances into a revenue-share structure." },
    ],
    faq: [
      { q: "How is revenue-based financing different from a loan", a: "RBF has no fixed monthly payment and (usually) no APR. You pay a fixed percentage of revenue until a pre-agreed total is delivered. It's a contract for future revenue, not a traditional loan." },
      { q: "Is RBF dilutive", a: "No. There's no equity component. You keep 100% ownership of your business." },
      { q: "How much can I raise with RBF", a: `Typical caps are 4-7× monthly recurring revenue or platform sales. ${A}` },
      { q: "What does RBF cost", a: "Usually expressed as a multiple (e.g., 1.3×): you repay 1.3× of the funded amount over the repayment period." },
      { q: "What kinds of business qualify", a: "Most active programs focus on SaaS, ecommerce, subscription, and recurring-service businesses with at least $10K/month in trackable revenue." },
    ],
  },

  "same-day-business-loans": {
    whatItIs: [
      "Same-day business loans are short-term, revenue-based products designed to fund within a single business day after approval. Underwriting is automated and driven by your business bank statements; tax returns and lengthy financial packages are not required.",
      "These are the right tool when you have a genuine emergency (equipment down, payroll today, a deal that closes this afternoon) and the alternative is losing real money. Speed has a cost; we'll always show you whether a slower, cheaper option is realistic before you sign.",
    ],
    bestForBusinesses: [
      "Restaurants, retail, and service businesses with daily revenue",
      "Owners facing an unexpected emergency expense",
      "Businesses too new or too credit-bruised for SBA",
      "Operators with steady deposits but limited collateral",
    ],
    situations: [
      "Equipment failure is costing you revenue every hour",
      "A vendor needs a deposit today to hold inventory or a date",
      "Payroll runs tomorrow and a customer payment is late",
      "You have an opportunity that won't be there next week",
    ],
    terms: {
      amount: "$5K - $150K",
      term: "3-18 months",
      speed: "Same business day after approval",
      qualifications: "3+ months operating, ~$8K+ monthly deposits, active business bank account",
      documentation: "1-page application + 3 months business bank statements",
    },
    commonUses: [
      { h: "Emergency equipment repair", p: "Pay a vendor today to restore a critical revenue-producing asset." },
      { h: "Cover today's payroll", p: "Pay the team while a customer payment is in transit." },
      { h: "Lock in a time-sensitive deal", p: "Put down a deposit on inventory, a contract, or a property before someone else does." },
      { h: "Cover an unexpected tax or insurance bill", p: "Smooth a one-time hit over daily revenue." },
    ],
    faq: [
      { q: "Can I really get funded the same day", a: "Yes, for files approved early in the day with a clean business bank account ready to receive wires. Late-day approvals usually fund next business morning." },
      { q: "What's the credit score requirement", a: `Most same-day programs approve on revenue with a 550+ FICO floor. Strong deposits can offset weaker credit. ${A}` },
      { q: "Are same-day loans expensive", a: "Yes. Speed has a real cost. They're the right tool when slower options aren't realistic and the cost of waiting is higher than the cost of the loan." },
      { q: "How much can I borrow same day", a: "Most same-day approvals run $5K-$150K, sized to about 1× monthly business deposits." },
      { q: "How is repayment structured", a: "Typically daily or weekly ACH from your business bank account, over a 3-18 month term." },
    ],
  },

  "business-lenders-near-me": {
    whatItIs: [
      "\"Lenders near me\" usually means one of two things: a local community bank that knows your market and offers relationship banking, or a Dallas-based advisor who can shop your file across a wider network of national lenders actively writing for Texas borrowers.",
      "Both have value. Local banks bring the relationship; an advisor brings choice. We're a Dallas-headquartered firm, so you get both: a local point of contact who knows Texas and underwrites your file across the right combination of community, regional, and national lenders.",
    ],
    bestForBusinesses: [
      "Dallas owners who want a Texas-based point of contact",
      "Businesses that prefer in-person meetings over web forms",
      "Owners who want a single advisor managing multiple lender relationships",
      "Borrowers comparing community bank vs. national lender offers",
    ],
    situations: [
      "You'd rather work with someone local than a faceless web portal",
      "Your bank stalled and you want a real human to drive the file",
      "You want to see local + national offers side-by-side",
      "You value relationship banking but also want competitive pricing",
    ],
    terms: {
      amount: "$10K - $5M+ depending on program",
      term: "Varies by lender and program",
      speed: "Varies · from 24 hours to 60 days",
      qualifications: "Vary by program; we match you to lenders most likely to approve your file",
      documentation: "1-page application minimum; full package for larger or SBA files",
    },
    commonUses: [
      { h: "Local relationship banking", p: "Build a long-term banking relationship with a Texas-focused lender." },
      { h: "Compare local vs. national pricing", p: "See where a community bank wins and where a national lender does." },
      { h: "Get a real local advisor on your file", p: "Stop bouncing between web portals and call centers." },
      { h: "Bring a Dallas address to underwriting", p: "Texas borrowers often clear local lenders' criteria faster than out-of-market files." },
    ],
    faq: [
      { q: "Are local lenders cheaper than online lenders", a: "Often yes for established borrowers. Community banks compete on relationship pricing. Online lenders win on speed and on borrowers community banks decline." },
      { q: "Do you only work with Dallas lenders", a: "No. We're Dallas-based, but we route your file to whatever lender (local, regional, or national) is most likely to approve and price your specific deal well." },
      { q: "Can I get a business loan with no in-person meeting", a: "Yes. We handle most files end-to-end remotely. In-person meetings happen only when you want them or when a specific lender requires them." },
      { q: "What's the advantage of working with an advisor vs. going direct", a: "One application, multiple lenders, side-by-side offers, and someone managing the back-and-forth. You don't pay us. The lender does, at close." },
      { q: "Are you licensed in Texas", a: `${A} ${AGENCY_NAME} operates as a Texas-based commercial finance advisor and routes regulated products through appropriately licensed partner lenders.` },
    ],
  },

  "business-loan-broker": {
    whatItIs: [
      "A business loan broker (more accurately, a commercial finance advisor) submits one application from you to multiple lenders, then brings back comparable offers so you can choose on facts rather than on whichever lender called first.",
      "Good brokers add value by matching you to lenders most likely to approve your specific file at the best price, negotiating prepayment and fee terms, and saving you the time of running a parallel process across 8-10 lenders yourself. Bad brokers add upfront fees and submit your file everywhere with no judgment. We don't do either.",
    ],
    bestForBusinesses: [
      "Owners who don't have time to run their own multi-lender process",
      "Borrowers who've been declined and don't know what's next",
      "Companies with complex files (recent dips, tax liens, prior MCAs)",
      "First-time borrowers who want guidance",
    ],
    situations: [
      "You want comparison, not a single take-it-or-leave-it offer",
      "Your file has a complication that needs a knowledgeable submission",
      "You don't want to give your data to 10 different web forms",
      "You want a single point of contact through funding",
    ],
    terms: {
      amount: "$5K - $5M+",
      term: "Varies by underlying lender program",
      speed: "24 hours - 60+ days depending on program",
      qualifications: "Depend on the products you qualify for",
      documentation: "1-page application + bank statements to start",
    },
    commonUses: [
      { h: "Shop your file efficiently", p: "One application becomes multiple comparable offers in 24-72 hours." },
      { h: "Place a complicated file", p: "Recent dips, tax liens, prior advances. We know which lenders will still quote." },
      { h: "Compare SBA, conventional, and short-term", p: "See the full menu of what you actually qualify for." },
      { h: "Negotiate fees and prepayment", p: "We push back on origination fees, prepayment penalties, and stacking restrictions." },
    ],
    faq: [
      { q: "How does a business loan broker get paid", a: "Reputable brokers are paid by the lender at closing, not by you. We don't take upfront fees, application fees, or success fees from borrowers." },
      { q: "Are brokers more expensive than going direct", a: "No. The lender's pricing to a broker-submitted file is typically the same as direct, because the broker fee is built into how the lender prices the channel, not added on top of your rate." },
      { q: "Will using a broker hurt my credit", a: "We use a soft pull at pre-qualification. Hard pulls happen only when you accept an offer and move to formal underwriting, not on every submission." },
      { q: "How is a broker different from a lender", a: "A lender funds the loan with their own (or warehoused) capital. A broker matches you to the right lender and manages the process. We are not a lender. We shop your file." },
      { q: "Should I also apply directly to my bank", a: "If you want. Running both in parallel doesn't hurt, and the resulting offer comparison usually gets you a better deal somewhere." },
    ],
  },

  "commercial-loan-broker": {
    whatItIs: [
      "A commercial loan broker structures and places larger commercial financing: commercial real estate, large equipment packages, business acquisitions, hospitality, and construction. These deals require deeper underwriting, capital-stack design, and lender relationships that go beyond the standard small-business product set.",
      "We work with banks, life-insurance companies, debt funds, CMBS originators, and SBA Preferred Lenders to place commercial transactions for Dallas-area borrowers, including blended capital stacks where a single lender can't take the whole deal.",
    ],
    bestForBusinesses: [
      "Owners financing commercial real estate ($1M+)",
      "Buyers in middle-market acquisitions",
      "Hospitality and hotel sponsors (acquisition, PIP, refinance)",
      "Commercial construction projects with permanent take-out",
      "Multi-unit franchisees with portfolio financing needs",
    ],
    situations: [
      "Your transaction is too large or complex for a standard small-business lender",
      "You need a blended capital stack (senior debt + mezz, or bank + SBA)",
      "You're refinancing maturing commercial debt",
      "You need an advisor to manage diligence with multiple capital sources",
    ],
    terms: {
      amount: "$250K - $25M+",
      term: "5-30 years depending on structure",
      speed: "30-90+ days from term sheet to close",
      qualifications: "Strong sponsor, documented cash flow, DSCR ≥ 1.2, equity/skin in the deal",
      documentation: "Full financial package, property/business diligence, sponsor REO and PFS",
    },
    commonUses: [
      { h: "Commercial real estate acquisitions", p: "Office, retail, industrial, and multifamily, for both owner-user and investor deals." },
      { h: "Hotel and hospitality financing", p: "Acquisition, brand-mandated PIP, refinance, and ground-up." },
      { h: "Large equipment and fleet packages", p: "Multi-asset financings with custom term and rate." },
      { h: "Middle-market acquisitions", p: "SBA 7(a) up to $5M plus conventional senior debt for larger deals." },
      { h: "Construction-to-perm", p: "Draw-based construction with conversion to permanent financing at completion." },
    ],
    faq: [
      { q: "What size deals do you place", a: "Typical commercial files run $250K to $25M, with larger transactions placed via our institutional partners." },
      { q: "How is commercial pricing structured", a: `Commercial deals are quoted on a per-deal basis based on sponsor strength, asset class, leverage, and DSCR. ${A}` },
      { q: "How long does a commercial loan take to close", a: "From signed term sheet, typical timeline is 45-90 days depending on diligence (appraisal, environmental, lender legal)." },
      { q: "Do you charge fees on commercial deals", a: "On larger transactions a placement fee is sometimes earned at close and disclosed up front in writing; for SBA and most business loans the lender pays us." },
      { q: "Can you finance ground-up construction", a: "Yes. Draw-based commercial construction loans with conversion to permanent financing at completion." },
    ],
  },

  "equipment-financing": {
    whatItIs: [
      "Equipment financing is a loan or lease used to acquire specific business equipment: vehicles, machinery, technology, kitchen, medical, or production gear. The equipment itself serves as collateral, which makes approvals easier than for an unsecured loan of the same size.",
      "For Dallas-area businesses, equipment financing is often the cheapest and easiest path to a new asset. The lender can repossess the equipment if things go wrong, so they take less credit risk and price accordingly. Terms typically match the useful life of the asset.",
    ],
    bestForBusinesses: [
      "Trucking, construction, and trades buying vehicles or machinery",
      "Restaurants and food service buying kitchen equipment",
      "Medical and dental practices buying imaging or chairs",
      "Manufacturers and fabricators buying production equipment",
      "Any business with a defined equipment purchase",
    ],
    situations: [
      "You have a specific piece of equipment in mind with a vendor quote",
      "You'd rather preserve cash and finance the asset over its useful life",
      "You want predictable monthly payments tied to a specific asset",
      "You want to take Section 179 depreciation in year one",
    ],
    terms: {
      amount: "$10K - $5M+ depending on asset",
      term: "2 - 7 years (matched to asset useful life)",
      speed: "2 - 7 business days",
      qualifications: "6+ months in business, vendor quote, owner with decent credit",
      documentation: "1-page application + vendor quote · larger files add financials",
    },
    commonUses: [
      { h: "Buy a work truck or van", p: "Get the asset on the road this week; finance over its useful life." },
      { h: "Add production capacity", p: "Bring on a second machine, lift, oven, or rig before turning down work." },
      { h: "Upgrade aging equipment", p: "Replace a maintenance-hungry unit with newer, more efficient equipment." },
      { h: "Buy used equipment", p: "Finance pre-owned assets from dealers, auctions, or private sellers." },
      { h: "Fund a build-out's equipment package", p: "Finance kitchen, salon, dental, or shop equipment as part of an opening." },
    ],
    faq: [
      { q: "Do I have to put money down", a: `Many equipment loans require 0-20% down depending on the asset and your credit. New equipment from a strong vendor often goes with $0 down. ${A}` },
      { q: "Can I finance used equipment", a: "Yes. Most lenders finance used equipment, though terms are shorter and pricing is typically a bit higher than new." },
      { q: "Should I lease or buy", a: "Lease if you want lower payments and plan to refresh equipment frequently; buy (finance) if you want to own the asset and take full depreciation. We can quote both." },
      { q: "What about Section 179 depreciation", a: "Equipment financed and placed in service in the same tax year is generally eligible for Section 179 deduction up to the annual limit. Talk to your CPA for your specifics." },
      { q: "Can a startup get equipment financing", a: "Yes. Because the equipment is collateral, newer businesses can often qualify when they wouldn't for an unsecured loan." },
    ],
  },

  /* ===================== VERTICAL PILLARS ===================== */

  "dental-practice-loans": {
    whatItIs: [
      "Dental practice loans are SBA 7(a), SBA 504, and conventional financing programs structured around how dental practices actually cash-flow. Lenders that specialize in dental understand that practices generate strong, predictable revenue, so they'll often lend at higher leverage and longer terms than a generalist bank would.",
      "For Dallas dentists, the most common uses are acquiring an existing practice, opening a second location (de novo), buying chairs and imaging equipment, and refinancing higher-cost debt into longer SBA terms.",
    ],
    bestForBusinesses: [
      "Associates buying their first practice",
      "Existing owners adding a second or third location",
      "Specialists (ortho, perio, endo, oral surgery) opening de novo",
      "Owners financing major equipment (CBCT, CAD/CAM, chairs)",
      "DSOs and partnerships acquiring solo practices",
    ],
    situations: [
      "You're acquiring or buying into an existing practice",
      "You want longer terms than your local bank will offer",
      "You're financing 100% practice + working capital in one note",
      "You're refinancing prior practice debt",
    ],
    terms: {
      amount: "$100K - $5M (SBA) · larger for conventional and DSO deals",
      term: "10 - 25 years",
      speed: "30 - 60 days to fund",
      qualifications: "DDS/DMD, 1+ year experience, decent personal credit, practice DSCR ≥ 1.2",
      documentation: "Practice financials (3 yrs), purchase agreement, owner PFS + tax returns",
    },
    commonUses: [
      { h: "Practice acquisition", p: "Up to 100% financing of purchase price plus working capital under SBA 7(a)." },
      { h: "De novo (start a practice from scratch)", p: "Equipment, build-out, and working capital runway in one loan." },
      { h: "Buy or upgrade equipment", p: "CBCT, CAD/CAM, chairs, sterilization, and IT, financed over useful life." },
      { h: "Buy your office building", p: "SBA 504 for owner-occupied real estate at long terms and high leverage." },
      { h: "Refinance prior practice debt", p: "Consolidate equipment, acquisition, and working capital debt under one SBA payment." },
    ],
    faq: [
      { q: "Can a new associate buy a practice", a: "Yes. SBA lenders that specialize in dental routinely fund first-time buyers with 1+ year of clinical experience and decent credit, often at 100% financing." },
      { q: "How much can I borrow for a dental practice", a: `Acquisitions and de novos commonly fund $500K - $3M+. The cap depends on practice cash flow, not just your personal credit. ${A}` },
      { q: "Do I need to put money down on a dental practice acquisition", a: "Dental-specialist SBA lenders often allow $0 down on acquisitions when DSCR is strong. Conventional lenders typically expect 10-20%." },
      { q: "How long does a dental practice loan take to close", a: "Typically 45-60 days from accepted LOI to funding for SBA acquisitions; faster for equipment-only files." },
      { q: "Can I finance real estate with my practice acquisition", a: "Yes. SBA 7(a) can wrap owner-occupied real estate into the practice acquisition on one note, or SBA 504 can finance the real estate separately at longer terms." },
    ],
  },

  "medical-practice-loans": {
    whatItIs: [
      "Medical practice loans are SBA and conventional programs designed around physician and clinic cash flow. Lenders that specialize in healthcare understand insurance reimbursement cycles, allowed-amounts vs. billed-amounts, and the long ramp on de novo clinics, so they underwrite differently than a generalist business lender.",
      "For Dallas-area physicians and groups, the most common uses are practice acquisition, partner buy-in/buy-out, opening additional locations, financing imaging or equipment, and acquiring owner-occupied real estate.",
    ],
    bestForBusinesses: [
      "Physicians acquiring an existing practice",
      "Specialists opening a de novo clinic",
      "Groups financing imaging, surgical, or office equipment",
      "Partners buying into or out of a partnership",
      "Practices acquiring owner-occupied real estate",
    ],
    situations: [
      "You're buying a practice or buying into a partnership",
      "You want longer-term, lower-payment SBA financing",
      "You're financing equipment with significant useful life",
      "You're consolidating prior practice debt",
    ],
    terms: {
      amount: "$100K - $5M (SBA) · higher conventional",
      term: "10 - 25 years depending on use",
      speed: "30 - 60 days",
      qualifications: "MD/DO/PA, decent personal credit, practice DSCR ≥ 1.2",
      documentation: "Practice financials, purchase docs, owner PFS + tax returns",
    },
    commonUses: [
      { h: "Practice acquisition", p: "Up to 100% financing for physicians buying an existing practice." },
      { h: "Partner buy-in / buy-out", p: "Fund an associate's buy-in or a senior partner's exit." },
      { h: "De novo clinic", p: "Build-out, equipment, and working capital for opening a new location." },
      { h: "Equipment financing", p: "Imaging, surgical, lab, and IT financed over useful life." },
      { h: "Owner-occupied real estate (504)", p: "Buy the building your clinic operates from at long terms and high leverage." },
    ],
    faq: [
      { q: "Can a W-2 physician get a practice loan", a: "Yes. Physician-specialist SBA lenders regularly fund first-time buyers transitioning from employment to ownership, including 100% financing for acquisitions." },
      { q: "How is medical practice loan underwriting different", a: "Specialist lenders model collections net of payer mix, write-offs, and reimbursement timing. A generalist bank often won't credit you for the practice's real cash generation." },
      { q: "Do I need real estate to qualify", a: "No. Most medical practice loans are working-capital, equipment, or acquisition based. Real estate is optional and often financed separately under SBA 504." },
      { q: "How quickly can a medical practice loan fund", a: "Equipment-only files in 1-2 weeks. SBA acquisitions in 45-60 days from complete file." },
      { q: "Can I refinance prior practice debt", a: `Yes. Consolidating equipment, acquisition, and working-capital balances into a single SBA note is a common use case. ${A}` },
    ],
  },

  "restaurant-loans": {
    whatItIs: [
      "Restaurant loans are financing programs that work around how restaurants actually cash-flow: lumpy daily revenue, thin margins, heavy equipment costs, and significant build-out expense. Specialty restaurant lenders underwrite to deposits and ticket count, not to a flat balance-sheet template.",
      "For Dallas-area restaurants we structure financing across the right mix: equipment financing for the kitchen, SBA 7(a) for acquisition or full build-out, working capital for ramp-up, and revenue-based products for marketing and short-term growth.",
    ],
    bestForBusinesses: [
      "Independent restaurant and bar owners",
      "Multi-unit franchisees adding locations",
      "Operators buying an existing restaurant",
      "Bars, cafés, fast-casual, and full-service concepts",
      "Catering and ghost-kitchen operators",
    ],
    situations: [
      "You're opening, expanding, or acquiring a restaurant",
      "Kitchen equipment is failing and needs to be replaced fast",
      "You need working capital through a slow season",
      "You're remodeling or refreshing the concept",
    ],
    terms: {
      amount: "$25K - $1M (typical) · up to $5M with SBA",
      term: "6 months - 25 years depending on program",
      speed: "Same day - 60 days depending on program",
      qualifications: "6+ months operating for revenue products · 2+ years for SBA",
      documentation: "Bank/processor statements + (for SBA) full financial package",
    },
    commonUses: [
      { h: "Build-out for a new location", p: "Cover GC draws, kitchen equipment, FF&E, and pre-opening payroll." },
      { h: "Replace failing kitchen equipment", p: "Get a key piece of equipment back online without losing service." },
      { h: "Cover slow-season working capital", p: "Smooth payroll and rent through a known dip." },
      { h: "Buy an existing restaurant", p: "Acquire a turnkey operation with cash flow already in place." },
      { h: "Remodel or rebrand", p: "Refresh dining room, signage, and POS to lift average ticket." },
    ],
    faq: [
      { q: "Can a new restaurant get a loan", a: "Pre-opening restaurants typically need SBA financing with an owner equity injection, owner experience, and a strong concept package. Personal-credit-backed lines can supplement." },
      { q: "What's the fastest way to fund kitchen equipment", a: `Equipment financing can fund in 2-5 business days against a vendor quote. ${A}` },
      { q: "Can I get a restaurant loan with daily card sales but weak credit", a: "Yes. Revenue-based products and MCAs approve on processor and deposit data with FICOs in the 550+ range." },
      { q: "How much working capital do restaurants need", a: "A common guideline is 3 months of fixed operating expenses; we'll size to your specific cost structure." },
      { q: "Do I need a liquor license to qualify", a: "No, but if you operate a bar and serve liquor, lenders will want to see the license and the TABC compliance history." },
    ],
  },

  "trucking-business-loans": {
    whatItIs: [
      "Trucking business loans cover the two biggest needs in the industry: equipment (trucks, trailers, reefer units, lift gates) and working capital to bridge the gap between delivering a load and getting paid for it. Most trucking-specialty lenders also offer freight factoring as part of the same relationship.",
      "For Dallas-area carriers and owner-operators, we package the right combination: equipment financing for tractors and trailers, a working capital line for fuel and maintenance, and freight factoring on broker invoices so you're paid on delivery instead of in 30-60 days.",
    ],
    bestForBusinesses: [
      "Owner-operators buying their first truck",
      "Small fleets adding tractors or trailers",
      "Carriers needing reliable fuel and maintenance cash flow",
      "Operators bridging long broker payment cycles",
      "Niche haul (reefer, flatbed, heavy haul) operators",
    ],
    situations: [
      "You're buying a truck or trailer this month",
      "You're waiting 30-60 days on broker payments",
      "Fuel and maintenance are eating into your cash buffer",
      "You're scaling from owner-operator to small fleet",
    ],
    terms: {
      amount: "$10K - $2M depending on use",
      term: "1 - 7 years (equipment) · revolving (factoring)",
      speed: "24 hours (factoring) · 2-7 days (equipment)",
      qualifications: "Valid CDL/MC authority, 3+ months operating, clean recent CSA",
      documentation: "Equipment quote · bank statements · MC# and authority docs",
    },
    commonUses: [
      { h: "Buy a tractor or trailer", p: "Get the equipment under your authority and on the road quickly." },
      { h: "Freight factoring on broker loads", p: "Get paid the day you deliver instead of waiting 30-60 days." },
      { h: "Fuel and maintenance line", p: "Stable revolving capacity for ongoing operating costs." },
      { h: "Add a second truck", p: "Equipment financing on truck #2 plus working capital for ramp-up." },
      { h: "Refinance high-payment equipment", p: "Reset a maxed truck note into a longer term and lower monthly payment." },
    ],
    faq: [
      { q: "Can a new owner-operator get a truck loan", a: "Yes. Equipment lenders that specialize in trucking will fund first-time owner-operators with valid authority, decent personal credit, and reasonable down payment." },
      { q: "How does freight factoring work for trucking", a: "You deliver the load, send the BOL to the factor, and get paid (typically) the same day. The factor then collects from the broker or shipper on standard terms." },
      { q: "How much down payment do I need on a truck", a: `Typical down is 10-25% on used trucks; new trucks from strong manufacturer programs can sometimes go with $0 down. ${A}` },
      { q: "Can I get a loan with new MC authority", a: "Yes. Factoring is available right after authority is granted. Equipment lenders typically want 3+ months under your own authority for the best pricing." },
      { q: "Do you finance reefer and specialized trailers", a: "Yes. Reefer, flatbed, lowboy, dump, and tanker financing through specialty lenders." },
    ],
  },

  "commercial-construction-loan": {
    whatItIs: [
      "Commercial construction loans are draw-based facilities used to fund ground-up construction or major renovation of commercial property. The lender funds in stages (\"draws\") tied to completed work and inspections, then converts to permanent financing at completion (\"construction-to-perm\") or pays off via a separate take-out loan.",
      "For Dallas-area projects we structure construction debt based on sponsor strength, project cost vs. as-completed value, contractor quality, and pre-leasing, and bring in mezzanine or preferred-equity layers when senior debt alone can't cover the project.",
    ],
    bestForBusinesses: [
      "Developers building ground-up commercial projects",
      "Owner-users building or expanding their operating facility",
      "General contractors building on their own account",
      "Owners undertaking major renovations or additions",
      "Sponsors converting use (office-to-multifamily, retail-to-medical)",
    ],
    situations: [
      "You're starting a new build or major renovation",
      "You need draws tied to construction progress, not a lump sum",
      "You want construction-to-perm rather than two separate closings",
      "You have signed leases or LOIs supporting take-out underwriting",
    ],
    terms: {
      amount: "$500K - $25M+",
      term: "12 - 36 months (construction) · 5-30 years (perm)",
      speed: "45 - 120 days to close",
      qualifications: "Sponsor experience, project budget + GC contract, equity contribution, take-out plan",
      documentation: "Project budget, plans + specs, GC contract, appraisal, sponsor financials",
    },
    commonUses: [
      { h: "Ground-up commercial construction", p: "Office, retail, industrial, medical, or hospitality from the ground up." },
      { h: "Owner-occupied build for your own business", p: "Build the headquarters or facility your business will operate out of." },
      { h: "Major renovations or additions", p: "Significant expansion of an existing building, financed in draws." },
      { h: "Adaptive reuse", p: "Convert one property type to another (office-to-multifamily, big-box to medical)." },
      { h: "Construction-to-perm", p: "Fund construction and roll into permanent financing without a second closing." },
    ],
    faq: [
      { q: "What loan-to-cost is typical", a: "Senior construction lenders generally cover 65-80% of total project cost on commercial deals, depending on asset class, sponsor, and pre-leasing." },
      { q: "How do construction draws work", a: "The GC submits a draw request tied to completed work. The lender's inspector verifies, then funds the draw less retainage. Draws repeat monthly through completion." },
      { q: "Do I need pre-leasing to get approved", a: "For speculative commercial builds, lenders increasingly require some level of pre-leasing or take-out commitment. Owner-user deals don't need it." },
      { q: "What's construction-to-perm", a: "One loan that funds construction in draws, then automatically converts to permanent amortizing financing at completion, avoiding the second closing and risk gap." },
      { q: "Can SBA finance ground-up construction", a: `Yes. SBA 504 and 7(a) can finance owner-occupied ground-up construction with attractive long-term economics. ${A}` },
    ],
  },

  "hotel-loans": {
    whatItIs: [
      "Hotel loans are commercial financing programs structured around hospitality cash flow, brand performance, and PIP (Property Improvement Plan) cycles. SBA 7(a) and 504 are heavily used for limited-service hotel acquisitions; conventional CMBS and bridge debt cover larger full-service and luxury deals.",
      "For Dallas-area hotel sponsors we structure acquisition, refinance, brand PIP, and bridge-to-perm financing across SBA, bank, debt fund, and CMBS lenders depending on flag, RevPAR trend, and sponsor experience.",
    ],
    bestForBusinesses: [
      "Hotel sponsors acquiring limited-service or boutique properties",
      "Owners executing a brand-mandated PIP",
      "Operators refinancing maturing hotel debt",
      "Hotel buyers in 1031 exchange timelines",
      "Sponsors converting underperforming assets between flags",
    ],
    situations: [
      "You're acquiring a hotel with a brand-required PIP",
      "You're refinancing pre-COVID debt that's maturing",
      "You're executing a flag change or repositioning",
      "You need bridge debt to stabilize before perm financing",
    ],
    terms: {
      amount: "$500K - $10M+ (SBA) · much larger conventional/CMBS",
      term: "10-25 years (SBA) · 5-10 years (CMBS) · 1-3 years (bridge)",
      speed: "60 - 120 days",
      qualifications: "Sponsor hotel experience, franchise approval, sufficient liquidity, asset DSCR ≥ 1.3",
      documentation: "STR/HotStats, P&L, brand approval, franchise docs, sponsor REO + PFS",
    },
    commonUses: [
      { h: "Hotel acquisition", p: "SBA 7(a) / 504 or conventional financing for limited-service and select-service hotel acquisitions." },
      { h: "PIP (Property Improvement Plan)", p: "Finance brand-mandated renovations as part of acquisition or refinance." },
      { h: "Refinance maturing hotel debt", p: "Roll maturing CMBS or bank debt into a new perm or bridge facility." },
      { h: "Flag change or repositioning", p: "Fund the conversion costs and stabilization runway." },
      { h: "Bridge to stabilization", p: "Short-term debt while the asset ramps to underwritable cash flow for permanent financing." },
    ],
    faq: [
      { q: "What's the max SBA hotel loan amount", a: "SBA 7(a) caps at $5M. Combining 7(a) with 504 or conventional structures can stretch total proceeds significantly higher." },
      { q: "Do I need prior hotel experience", a: "Strongly preferred. First-time hotel sponsors often need a co-borrower with hospitality experience or a strong third-party management contract." },
      { q: "Can I finance the PIP as part of the acquisition", a: `Yes. SBA and conventional acquisition loans routinely include PIP funds as part of the use of proceeds. ${A}` },
      { q: "What flags do lenders prefer", a: "Major branded flags (Marriott, Hilton, IHG, Hyatt, Choice) get the best pricing. Independent and soft-brand hotels can still finance but pricing reflects the additional risk." },
      { q: "How long does a hotel loan take to close", a: "SBA 60-90 days; conventional 60-120 days; bridge debt 30-60 days." },
    ],
  },

  "salon-business-loans": {
    whatItIs: [
      "Salon business loans cover the most common needs for salon, spa, and barbershop owners: build-out for a new location, equipment (chairs, dryers, treatment beds), inventory (retail product), and working capital for slow seasons or marketing pushes.",
      "For Dallas-area salons we mix SBA (for acquisition and larger build-outs), equipment financing (for chairs and capital equipment), and short-term working-capital products (for cash-flow needs and marketing).",
    ],
    bestForBusinesses: [
      "Hair salons, barbershops, and nail salons",
      "Day spas, med spas, and massage studios",
      "Salon suite operators",
      "Multi-location salon groups",
      "Booth-rental and chair-rental operators",
    ],
    situations: [
      "You're opening a second location or expanding the current one",
      "You're upgrading chairs, dryers, or treatment equipment",
      "You're buying an existing salon",
      "You need working capital for a slow stretch or a marketing push",
    ],
    terms: {
      amount: "$10K - $500K (typical) · up to $5M with SBA",
      term: "6 months - 25 years depending on program",
      speed: "24 hours - 60 days depending on program",
      qualifications: "6+ months operating · 2+ years for SBA · steady deposits",
      documentation: "Bank statements + (for SBA) full financial package",
    },
    commonUses: [
      { h: "Build-out a new location", p: "Cover lease deposits, build-out, chairs, and opening payroll." },
      { h: "Buy an existing salon", p: "SBA acquisition financing for turnkey salons with verifiable cash flow." },
      { h: "Upgrade chairs and equipment", p: "Finance the asset package over its useful life." },
      { h: "Stock retail product", p: "Pre-buy retail inventory for the season." },
      { h: "Marketing and rebrand", p: "Fund a campaign or rebrand and repay from the resulting traffic." },
    ],
    faq: [
      { q: "Can a new salon get a loan", a: "Pre-opening salons typically need SBA financing with an owner equity injection and experience in the industry. Equipment financing is also available for the chair and dryer package." },
      { q: "How much does it cost to open a salon in Dallas", a: `Costs vary widely by build-out scope and location. Typical full build-outs run $75K-$300K including equipment. ${A}` },
      { q: "Can I get financing as a booth renter", a: "Yes. If you operate as a registered business with steady deposits, working-capital and equipment financing are both available." },
      { q: "Do I need to pledge personal assets", a: "Most salon loans under $250K are unsecured at the asset level. Owners sign a personal guarantee, and a UCC may be filed on general business assets." },
      { q: "How fast can a salon loan fund", a: "Working-capital products fund in 24-72 hours; equipment in 2-5 business days; SBA in 30-60 days." },
    ],
  },

  "retail-business-loans": {
    whatItIs: [
      "Retail business loans are financing programs structured around how retail actually operates: seasonal revenue swings, large inventory positions, build-out and FF&E costs, and the need to fund a busy season before it arrives. Common structures are inventory-supported lines of credit, seasonal working capital, MCAs against card sales, and SBA for acquisitions and real estate.",
      "For Dallas-area retailers we'll structure the right mix: a line of credit for ongoing inventory cycles, working capital for seasonal pre-buys, and SBA when you're acquiring or expanding a brick-and-mortar concept.",
    ],
    bestForBusinesses: [
      "Brick-and-mortar specialty retail and boutiques",
      "Multi-channel retailers (in-store + ecommerce)",
      "Seasonal retailers (gift, outdoor, apparel)",
      "Furniture, appliance, and big-ticket retailers",
      "Multi-unit retail operators",
    ],
    situations: [
      "You're stocking inventory ahead of a busy season",
      "You need a line that grows with your inventory turn",
      "You're opening a second store",
      "You're remodeling or refreshing the storefront",
    ],
    terms: {
      amount: "$10K - $500K (typical) · up to $5M with SBA",
      term: "6 months - 25 years depending on program",
      speed: "Same day - 60 days depending on program",
      qualifications: "6+ months operating · steady deposits · POS/processor history",
      documentation: "Bank/processor statements + (for SBA) full package",
    },
    commonUses: [
      { h: "Pre-season inventory buy", p: "Pre-fund Q4 inventory in August/September to capture better unit pricing." },
      { h: "Open a second store", p: "Build-out, FF&E, opening inventory, and ramp-up payroll." },
      { h: "Remodel the storefront", p: "Fixtures, lighting, signage, and POS upgrades that lift average ticket." },
      { h: "Fund a marketing push", p: "Cover an ad campaign or grand-opening event." },
      { h: "Refinance high-cost debt", p: "Consolidate stacked short-term advances into a longer-term loan." },
    ],
    faq: [
      { q: "How much inventory financing can I get", a: "Inventory lines typically advance 50-70% of eligible inventory cost. We'll quote what the right structure looks like for your turn." },
      { q: "Can I borrow against my card sales", a: `Yes. Merchant cash advance and revenue-based products approve on card and deposit volume, often funding same day. ${A}` },
      { q: "Can I get a retail loan with bad credit", a: "Revenue-based products and MCAs approve on deposits and processor volume with FICOs as low as 550, though pricing reflects the additional risk." },
      { q: "How fast can a retail loan fund", a: "Same-day to 72 hours for revenue-based products. 2-7 days for equipment. 30-60 days for SBA." },
      { q: "Will a retail loan affect my credit", a: "Pre-qualification uses a soft pull. Hard pulls happen only when you accept and move to formal underwriting." },
    ],
  },

  "auto-repair-shop-financing": {
    whatItIs: [
      "Auto repair shop financing covers the specific capital needs of independent auto, body, tire, and quick-lube shops: lifts and diagnostic equipment, bay expansion, parts inventory, and working capital through slow stretches. Equipment financing dominates the asset side; SBA and conventional working-capital products cover the rest.",
      "For Dallas-area shops we'll quote equipment financing for new lifts, alignment machines, and scan tools, plus a working-capital line for parts inventory and a longer SBA or conventional loan for build-outs, acquisitions, or owner-occupied real estate.",
    ],
    bestForBusinesses: [
      "Independent auto repair shops",
      "Body and collision shops",
      "Tire and wheel shops",
      "Quick-lube and oil-change operators",
      "Transmission and specialty repair shops",
    ],
    situations: [
      "You're adding a lift or a bay",
      "You're acquiring a turnkey shop",
      "You need a parts inventory line for major repairs",
      "You're moving into a larger facility",
    ],
    terms: {
      amount: "$10K - $500K (typical) · up to $5M with SBA",
      term: "6 months - 25 years depending on program",
      speed: "Same day - 60 days",
      qualifications: "6+ months operating · steady deposits · 2+ years for SBA",
      documentation: "Bank statements + (for SBA) full package",
    },
    commonUses: [
      { h: "Buy lifts and bay equipment", p: "Add capacity with new 2-post, 4-post, or alignment lifts." },
      { h: "Upgrade diagnostic equipment", p: "Modern scan tools and OEM-level diagnostics for late-model vehicles." },
      { h: "Build out an additional bay", p: "Fund the GC work and equipment to add productive square footage." },
      { h: "Acquire a shop", p: "SBA financing for shops with documented cash flow and a clean lease or real estate." },
      { h: "Buy the building", p: "SBA 504 for owner-occupied repair shop real estate." },
    ],
    faq: [
      { q: "Can I finance shop equipment with bad credit", a: `Equipment is collateral, so shops with weaker credit can often still qualify, especially for lifts and tire/alignment equipment from established vendors. ${A}` },
      { q: "How long does shop equipment financing take", a: "Most equipment files fund within 2-5 business days from a vendor quote." },
      { q: "Can I finance used lifts and equipment", a: "Yes. Most lenders finance used equipment from dealers, though pricing is typically a bit higher and terms a bit shorter than new." },
      { q: "Can I get working capital for parts inventory", a: "Yes. Short-term and revenue-based products are commonly used for parts cash flow ahead of large jobs." },
      { q: "Can I buy the shop building under SBA", a: "Yes. SBA 504 is purpose-built for owner-occupied real estate at long terms and high leverage (up to 90% LTV)." },
    ],
  },

  "ecommerce-business-funding": {
    whatItIs: [
      "Ecommerce business funding is structured around platform data: Shopify, Amazon, WooCommerce, BigCommerce, and the major processors (Stripe, PayPal). Lenders connect directly to your platform, read your sales history, and underwrite based on real revenue trends rather than tax returns.",
      "Common products are revenue-based financing (repay as a % of sales), inventory financing (advance against SKUs), and platform-native cash advances. For Dallas-area DTC, Amazon, and Shopify brands we'll quote across the active funders and show the real cost on each.",
    ],
    bestForBusinesses: [
      "DTC brands on Shopify, WooCommerce, or BigCommerce",
      "Amazon sellers (FBA and FBM)",
      "Multi-channel ecommerce operators",
      "Subscription-box businesses",
      "Wholesale + ecommerce hybrid brands",
    ],
    situations: [
      "You need to pre-buy inventory ahead of a launch or season",
      "You're scaling paid acquisition and need ad budget",
      "Your tax returns don't reflect today's trajectory",
      "You'd rather flex payments with revenue than commit to a fixed monthly",
    ],
    terms: {
      amount: "$10K - $1M",
      term: "3 - 36 months (RBF) · revolving (inventory)",
      speed: "24 - 72 hours after platform connection",
      qualifications: "6+ months on platform · ~$10K+ monthly platform revenue",
      documentation: "Read-only platform connection (Shopify/Amazon/Stripe) · bank statements",
    },
    commonUses: [
      { h: "Inventory pre-buy", p: "Stock SKUs ahead of a launch, holiday, or supply-chain delay." },
      { h: "Ad spend scaling", p: "Fund Meta, Google, and Amazon Ads now and repay from the resulting revenue." },
      { h: "Bridge an Amazon payout cycle", p: "Cover supplier payments while waiting on a 14-day Amazon disbursement." },
      { h: "Launch a new product line", p: "Fund manufacturing, packaging, and launch marketing." },
      { h: "Refinance high-cost MCAs", p: "Convert daily-payment advances into revenue-share or longer-term structures." },
    ],
    faq: [
      { q: "What platforms do you connect to", a: "Shopify, Amazon, WooCommerce, BigCommerce, Stripe, PayPal, and Square are most common. Other platforms can usually be supported via bank statement underwriting." },
      { q: "Is connecting my platform safe", a: "Yes. Funders use read-only API connections that pull sales data but cannot make changes to your store." },
      { q: "How much can I borrow as an ecommerce brand", a: `Typical caps run 4-7× monthly platform revenue for RBF; inventory lines depend on SKU-level eligibility. ${A}` },
      { q: "Can Amazon-only sellers qualify", a: "Yes. Amazon sellers are one of the largest customer segments for ecommerce funding, with several lenders specializing in it." },
      { q: "What's the difference between RBF and an MCA", a: "RBF is typically longer-term, lower cost, and lender-funded with read-only platform data. MCAs are faster, more expensive, and rely on daily holdbacks. We'll quote both." },
    ],
  },

  "food-truck-financing": {
    whatItIs: [
      "Food truck financing covers the two big costs in starting or growing a food-truck operation: the truck itself (with build-out and equipment) and the working capital needed for permits, commissary, opening inventory, and the first few months of operations.",
      "For Dallas-area operators we mix equipment financing for the truck and build-out, working-capital products for permits and commissary deposits, and (for established operators) SBA for expansion into a second truck or brick-and-mortar location.",
    ],
    bestForBusinesses: [
      "First-time food-truck operators",
      "Existing operators adding a second or third truck",
      "Catering and event operators",
      "Operators expanding from a truck to a brick-and-mortar concept",
      "Mobile coffee, dessert, and specialty concepts",
    ],
    situations: [
      "You're buying or building out your first truck",
      "You're adding a second truck to capture more events",
      "You need working capital for permits and opening inventory",
      "You're expanding from mobile-only to a fixed location",
    ],
    terms: {
      amount: "$25K - $250K (typical)",
      term: "2 - 7 years (equipment) · 6 - 36 months (working capital)",
      speed: "2 - 10 business days",
      qualifications: "Decent personal credit + business plan for first-time · 6+ months for working capital",
      documentation: "Vendor quote + business plan · bank statements for working capital",
    },
    commonUses: [
      { h: "Buy and build out the truck", p: "Finance truck purchase, kitchen build-out, and graphics package." },
      { h: "Permits and commissary deposits", p: "Cover health permits, commissary rent, and city fees." },
      { h: "Opening inventory", p: "Fund first-month food cost and disposables." },
      { h: "Add a second truck", p: "Equipment financing for truck #2 plus working capital for ramp-up." },
      { h: "Expand to brick-and-mortar", p: "Use truck cash flow as the foundation for SBA financing on a fixed location." },
    ],
    faq: [
      { q: "Can a brand-new operator finance a food truck", a: `Yes. Equipment financing is available for first-time operators with decent personal credit and a documented business plan. ${A}` },
      { q: "How much down payment is required", a: "Typical down on a built-out food truck is 10-20% for first-time operators; established operators sometimes go with $0 down." },
      { q: "Can I finance a used food truck", a: "Yes. Most lenders finance used trucks. Pricing and term reflect the age and condition of the truck and build-out." },
      { q: "What about kitchen equipment inside the truck", a: "Bundled into the truck loan or financed separately, depending on the vendor and your preferences." },
      { q: "How fast can a food truck deal fund", a: "From a vendor quote, most equipment files fund in 5-10 business days." },
    ],
  },

  "loan-for-gym-business": {
    whatItIs: [
      "Gym and fitness business loans are financing programs structured around the specific economics of gyms: heavy equipment cost, build-out expense, recurring-revenue membership models, and a meaningful ramp to break-even. Common structures are equipment financing for the gear, SBA for build-outs and acquisitions, and working capital for marketing and member acquisition.",
      "For Dallas-area gyms (independent, franchise, boutique, and specialty: CrossFit, yoga, Pilates, martial arts) we'll structure the right mix and bring in SBA when timing allows for the best long-term economics.",
    ],
    bestForBusinesses: [
      "Independent gyms and fitness studios",
      "Boutique concepts (HIIT, cycling, yoga, Pilates, barre)",
      "Franchise gyms (Anytime Fitness, OTF, F45, Pure Barre, etc.)",
      "Martial arts and combat sports studios",
      "Multi-location operators",
    ],
    situations: [
      "You're opening, expanding, or acquiring a gym",
      "You're refreshing or expanding the equipment package",
      "You need working capital for a member-acquisition push",
      "You're buying out a partner or another studio",
    ],
    terms: {
      amount: "$25K - $1M (typical) · up to $5M with SBA",
      term: "3 - 25 years depending on program",
      speed: "2 days - 60 days depending on program",
      qualifications: "6+ months operating · 2+ years for SBA · franchise approval where applicable",
      documentation: "Equipment quote + bank statements · full package for SBA",
    },
    commonUses: [
      { h: "Equipment package financing", p: "Strength, cardio, and accessory equipment financed over useful life." },
      { h: "Build-out a new location", p: "Cover GC draws, flooring, mirrors, FF&E, and pre-opening payroll." },
      { h: "Open a franchise unit", p: "SBA financing for franchise fee, build-out, equipment, and working capital." },
      { h: "Acquire a competitor or partner studio", p: "Roll up another studio or buy out a co-owner." },
      { h: "Marketing and member acquisition", p: "Fund a launch or growth campaign and repay from the membership lift." },
    ],
    faq: [
      { q: "Can a new gym get a loan", a: "Pre-opening gyms typically need SBA financing with an owner equity injection and a documented plan, plus equipment financing for the gear package." },
      { q: "How much does it cost to open a gym", a: `Costs vary widely. Boutique studios often run $150K-$400K all-in; franchise gyms can run higher depending on flag. ${A}` },
      { q: "Is the franchise on the SBA Franchise Directory", a: "Most major fitness franchises are listed and SBA-eligible. We confirm directory status before submission." },
      { q: "Can I finance used gym equipment", a: "Yes. Most equipment lenders finance used commercial gym equipment from established vendors or auction houses." },
      { q: "How fast can a gym loan fund", a: "Equipment financing in 2-7 business days; working capital in 24-72 hours; SBA in 30-60 days." },
    ],
  },

  "manufacturing-equipment-financing": {
    whatItIs: [
      "Manufacturing equipment financing covers acquisition of production machinery: CNC machines, presses, mills, welders, robotics, conveyors, packaging lines, and material-handling equipment. Terms are typically matched to the long useful life of the asset, which keeps monthly payments low relative to the equipment cost.",
      "For Dallas-area manufacturers and fabricators we'll structure equipment financing on new or used machinery, blanket facilities for ongoing acquisitions, and (for owner-occupied production real estate) SBA 504 with long-term low-payment economics.",
    ],
    bestForBusinesses: [
      "Job shops and contract manufacturers",
      "Fabricators (metal, plastics, composites)",
      "Food and beverage producers",
      "Packaging and labeling operations",
      "Robotics, automation, and CNC machine shops",
    ],
    situations: [
      "You're adding capacity for a new contract",
      "You're upgrading aging equipment to lift margin",
      "You're financing a complete production cell or line",
      "You're buying used equipment at auction or from a dealer",
    ],
    terms: {
      amount: "$25K - $5M+",
      term: "3 - 10 years (matched to asset life)",
      speed: "3 - 10 business days",
      qualifications: "1+ year operating · vendor quote · decent owner credit",
      documentation: "Vendor quote · 6 months bank statements · larger files add financials",
    },
    commonUses: [
      { h: "Add a CNC machine or cell", p: "Bring on a new mill, lathe, or full machining cell to expand capacity." },
      { h: "Upgrade to higher-throughput equipment", p: "Replace aging machinery with newer units that lower per-part cost." },
      { h: "Automation and robotics", p: "Finance pick-and-place, cobots, and integrated automation packages." },
      { h: "Packaging or filling lines", p: "Complete-line financing for food, beverage, or product packaging." },
      { h: "Material handling", p: "Forklifts, conveyors, racking, and warehouse equipment." },
    ],
    faq: [
      { q: "Can I finance used manufacturing equipment", a: `Yes. Most lenders finance used equipment from dealers, auction houses, and OEMs. Pricing and term reflect age and remaining useful life. ${A}` },
      { q: "Do you finance imported equipment", a: "Yes, including European and Asian OEM equipment, as long as a qualified installer and parts/service path exist in the U.S." },
      { q: "Can I bundle install and tooling into the loan", a: "Yes. Install, tooling, freight, and tax can usually be financed alongside the equipment cost." },
      { q: "What about Section 179", a: "Manufacturing equipment placed in service in the same tax year is generally Section 179 eligible up to annual limits. Confirm with your CPA." },
      { q: "What if I'm a new manufacturer with one big contract", a: "We can often finance the equipment needed to fulfill the contract using a combination of equipment financing and working capital, secured by the contract and the asset." },
    ],
  },

  "agriculture-equipment-financing": {
    whatItIs: [
      "Agriculture equipment financing covers the heavy assets that ag operations run on: tractors, combines, balers, planters, sprayers, hay equipment, irrigation systems, and grain storage. Lenders that specialize in ag understand seasonal cash flow and offer payment structures tied to harvest revenue.",
      "For Texas-area ag operators we'll structure financing on new or used equipment from major OEMs (John Deere, Case IH, New Holland, Kubota) and independent dealers, with seasonal or annual-pay structures available on most programs.",
    ],
    bestForBusinesses: [
      "Row crop and grain producers",
      "Cattle and livestock operations",
      "Dairy and poultry operations",
      "Hay, forage, and specialty crop producers",
      "Custom harvesters and ag service operators",
    ],
    situations: [
      "You're acquiring a new or used tractor, combine, or implement",
      "You need seasonal or annual payment structures tied to harvest",
      "You're upgrading irrigation or storage infrastructure",
      "You're expanding acreage and need more equipment to operate it",
    ],
    terms: {
      amount: "$25K - $2M+",
      term: "3 - 7 years (typical)",
      speed: "3 - 10 business days",
      qualifications: "1+ year operating · vendor quote · ag experience",
      documentation: "Vendor quote · Schedule F or business financials · proof of operation",
    },
    commonUses: [
      { h: "Buy a tractor or combine", p: "Finance new or used equipment from major OEMs and independent dealers." },
      { h: "Add implements", p: "Planters, sprayers, balers, mowers, and tillage equipment." },
      { h: "Upgrade irrigation", p: "Center pivot, drip, and pump-station financing." },
      { h: "Build or upgrade grain storage", p: "Bins, dryers, and handling equipment with longer-term financing." },
      { h: "Replace aging equipment", p: "Trade up to newer units with lower maintenance and fuel cost." },
    ],
    faq: [
      { q: "Do you offer seasonal payments", a: "Yes. Most ag equipment lenders offer annual-pay, semi-annual, or skip-payment structures matched to harvest cash flow." },
      { q: "Can I finance used ag equipment", a: `Yes. Used ag equipment from dealers and auctions is routinely financed. ${A}` },
      { q: "Do you finance equipment from independent dealers", a: "Yes, in addition to John Deere, Case IH, New Holland, and Kubota dealer programs." },
      { q: "Can a young farmer with limited credit qualify", a: "Yes. Equipment is collateral. Beginning-farmer programs and FSA-guaranteed structures can also widen access." },
      { q: "How fast does ag equipment financing fund", a: "Most files fund in 5-10 business days from a vendor quote." },
    ],
  },

  "landscaping-equipment-financing": {
    whatItIs: [
      "Landscaping equipment financing covers the mowers, trucks, trailers, skid steers, irrigation gear, and small attachments that landscape and lawn-care businesses run on. Equipment is collateral, so approvals are easier than for unsecured working capital, and seasonal payment structures are commonly available.",
      "For Dallas-area landscape operators we finance new and used equipment from major OEMs (Exmark, Toro, Scag, Kubota, Bobcat) and dealer networks, plus trucks and trailers for crew transport.",
    ],
    bestForBusinesses: [
      "Residential and commercial landscape contractors",
      "Lawn-care and maintenance companies",
      "Tree care and arborists",
      "Hardscape and irrigation specialists",
      "Snow-and-ice contractors (seasonal)",
    ],
    situations: [
      "You're adding crew capacity for the season",
      "You're replacing aging mowers and equipment",
      "You need a truck-and-trailer package for a new crew",
      "You're adding skid steers, mini excavators, or larger machinery",
    ],
    terms: {
      amount: "$10K - $500K",
      term: "2 - 6 years",
      speed: "2 - 7 business days",
      qualifications: "6+ months operating · vendor quote · decent owner credit",
      documentation: "Vendor quote · bank statements",
    },
    commonUses: [
      { h: "Buy commercial mowers", p: "Stand-ons, zero-turns, and walk-behinds for new crews." },
      { h: "Add a truck and trailer", p: "Outfit a new crew with a full vehicle and trailer package." },
      { h: "Buy a skid steer or mini excavator", p: "Add hardscape and grading capacity." },
      { h: "Upgrade irrigation and small tools", p: "Pumps, controllers, blowers, and string-trimmers as part of an asset package." },
      { h: "Snow-and-ice equipment", p: "Plows, salters, and spreaders financed ahead of the season." },
    ],
    faq: [
      { q: "Can I finance a complete mower-plus-trailer package", a: `Yes. Full crew packages (truck, trailer, mowers, and small tools) are commonly financed in a single transaction. ${A}` },
      { q: "Do you offer seasonal payments", a: "Yes. Many landscape equipment lenders offer skip-payment structures that match seasonal cash flow." },
      { q: "Can I finance used equipment", a: "Yes. Used commercial landscape equipment from dealers or auctions is routinely financed." },
      { q: "How much down payment is required", a: "Often $0-10% on new equipment from a strong vendor; 10-20% on used or specialty equipment." },
      { q: "How fast can a landscape equipment loan fund", a: "Most files fund in 2-7 business days from a vendor quote." },
    ],
  },

  "farm-credit-financing": {
    whatItIs: [
      "Farm credit financing is the broader category of debt that agricultural producers use to run and grow their operations: operating lines for seed, fertilizer, and inputs; equipment financing for machinery; and farm real-estate loans for land and improvements. Specialty ag lenders (including the Farm Credit System and ag-focused banks) underwrite to crop cycles, livestock cash flow, and seasonal payment patterns.",
      "For Texas-area producers we structure operating lines, equipment financing, and real-estate debt across the right mix of conventional, FSA-guaranteed, and Farm Credit-aligned lenders.",
    ],
    bestForBusinesses: [
      "Row crop and grain producers",
      "Cattle, livestock, and poultry operations",
      "Dairy and integrated ag operations",
      "Specialty crop and orchard producers",
      "Ranches and family farms",
    ],
    situations: [
      "You need an operating line for inputs and labor",
      "You're acquiring additional land or expanding acreage",
      "You're financing major equipment or infrastructure",
      "You're refinancing higher-cost ag debt",
    ],
    terms: {
      amount: "$25K - $5M+ depending on use",
      term: "1 year (operating) · 5-10 years (equipment) · 15-30 years (real estate)",
      speed: "1 - 6 weeks depending on program",
      qualifications: "Documented operation, schedule F or business financials, decent credit",
      documentation: "Tax returns, Schedule F, balance sheet, equipment or real-estate docs",
    },
    commonUses: [
      { h: "Operating line for inputs", p: "Annual line for seed, fertilizer, chemicals, fuel, and labor." },
      { h: "Equipment financing", p: "Tractors, combines, implements, and irrigation infrastructure." },
      { h: "Land purchase or expansion", p: "Long-term financing for additional acreage." },
      { h: "Farm real-estate refinance", p: "Roll older or higher-cost real-estate debt into longer, lower-payment structures." },
      { h: "Beginning-farmer financing", p: "FSA-guaranteed structures for new and growing operations." },
    ],
    faq: [
      { q: "What is the Farm Credit System", a: "A nationwide network of borrower-owned lending cooperatives that lend exclusively to agricultural and rural borrowers. They're a significant source of ag credit alongside ag-focused commercial banks." },
      { q: "Do you offer seasonal or annual payments", a: `Yes. Operating lines typically renew annually, and most equipment and real-estate financing offer annual or skip-payment structures aligned to harvest. ${A}` },
      { q: "Can a beginning farmer get financing", a: "Yes. FSA Direct and Guaranteed programs are designed for beginning farmers, often with reduced down-payment requirements and longer terms." },
      { q: "How fast can farm credit fund", a: "Operating lines typically set up in 2-4 weeks; equipment in 1-2 weeks; real estate in 4-8 weeks." },
      { q: "Can I finance both equipment and real estate together", a: "Yes, though they're typically structured as separate notes at different terms matched to each asset's useful life." },
    ],
  },

  "sba-loan-for-daycare": {
    whatItIs: [
      "SBA loans for daycare and childcare businesses are SBA 7(a) and 504 programs structured for licensed childcare operators. SBA lenders that specialize in childcare understand state licensing, enrollment cycles, and the typical operating economics of in-home, center-based, and franchise daycare.",
      "For Dallas/Texas operators we package SBA financing for acquisition of existing centers, de novo (start-up) financing for new centers, equipment, and owner-occupied real estate, including SBA 504 for buying the building.",
    ],
    bestForBusinesses: [
      "Owners acquiring an existing licensed daycare or childcare center",
      "Operators opening a new center (de novo)",
      "Existing operators adding a second or third location",
      "Franchise childcare operators (Kiddie Academy, Goddard, Primrose, etc.)",
      "Faith-based and after-school operators expanding into full-service",
    ],
    situations: [
      "You're buying an existing licensed center",
      "You're opening a new center and need build-out + working capital",
      "You're acquiring the building you operate out of",
      "You're refinancing prior childcare-business debt",
    ],
    terms: {
      amount: "$100K - $5M (SBA 7a) · larger combined with 504",
      term: "10 - 25 years",
      speed: "45 - 90 days",
      qualifications: "Licensed or license-eligible · 2+ years industry experience preferred · decent credit",
      documentation: "Business financials, enrollment data, licensing docs, owner PFS + tax returns",
    },
    commonUses: [
      { h: "Acquire an existing daycare", p: "Up to 90% financing on the purchase plus working capital under SBA 7(a)." },
      { h: "Open a de novo center", p: "Build-out, FF&E, licensing, and pre-opening payroll." },
      { h: "Buy or build the building", p: "SBA 504 for owner-occupied childcare real estate at long terms." },
      { h: "Open a franchise unit", p: "Franchise fee, branded equipment, and build-out for SBA-eligible childcare franchises." },
      { h: "Refinance prior debt", p: "Consolidate equipment and acquisition debt into one longer-term SBA payment." },
    ],
    faq: [
      { q: "Do daycares qualify for SBA loans", a: "Yes. Licensed daycare and early-childhood-education businesses are SBA-eligible, and several lenders specialize in the segment." },
      { q: "Can I get an SBA loan for a new daycare", a: `Yes. De novo daycare financing is available with owner equity injection, industry experience, and a documented enrollment plan. ${A}` },
      { q: "How much down payment is needed", a: "SBA typically expects 10-20% borrower equity on acquisitions and de novo financing." },
      { q: "Can I finance the real estate too", a: "Yes. Under SBA 7(a) as part of an acquisition, or separately under SBA 504 for higher leverage and longer terms on the real estate component." },
      { q: "What documentation will I need", a: "State license (or license application), enrollment data, 3 years business financials for acquisitions, owner personal financial statement and tax returns." },
    ],
  },

  "sba-loan-for-franchise": {
    whatItIs: [
      "SBA loans for franchise purchases are SBA 7(a) and 504 financing structured around franchises listed on the SBA Franchise Directory. Listed franchises have pre-approved franchise agreements, which speeds up the SBA approval process and lets the lender focus on the borrower and the unit economics.",
      "For Dallas/Texas franchisees (first-time buyers, multi-unit operators, and re-sales) we confirm directory eligibility, package the file, and place it with SBA Preferred Lenders that actively write in your specific brand.",
    ],
    bestForBusinesses: [
      "First-time franchisees in food service, fitness, services, and retail",
      "Multi-unit operators adding a second or third location",
      "Buyers acquiring an existing franchise unit (resale)",
      "Conversion franchisees joining an established brand",
      "Veterans qualifying for SBA Express franchise programs",
    ],
    situations: [
      "Your franchise is listed on the SBA Franchise Directory",
      "You have the required liquidity and net worth for the brand",
      "You can document operational or transferable management experience",
      "You're acquiring a resale with established cash flow",
    ],
    terms: {
      amount: "Up to $5M (7a) · larger combined with 504",
      term: "10 years (working capital) · up to 25 years (with real estate)",
      speed: "45 - 90 days",
      qualifications: "10%+ buyer equity, FDD-approved franchise on SBA Directory, decent credit",
      documentation: "Franchise FDD, signed franchise agreement, business plan, personal financials",
    },
    commonUses: [
      { h: "Buy your first franchise unit", p: "Franchise fee, build-out, equipment, and working capital in one SBA loan." },
      { h: "Add an additional unit", p: "Finance unit #2 or #3 with existing-operator economics." },
      { h: "Acquire an existing franchise unit (resale)", p: "Buy a unit with documented cash flow already in place." },
      { h: "Combine real estate + franchise", p: "Roll owner-occupied real estate into the franchise acquisition under 7(a) or 504." },
      { h: "Veteran SBA Express", p: "Faster SBA Express financing with reduced fees for qualifying veteran franchisees." },
    ],
    faq: [
      { q: "Is my franchise eligible for an SBA loan", a: `Check the SBA Franchise Directory. If your brand is listed, it's pre-approved. We confirm directory status before submission. ${A}` },
      { q: "How much money do I need to put down", a: "SBA generally expects 10-20% equity injection on franchise acquisitions, plus the franchise's minimum liquidity and net-worth requirements." },
      { q: "Can I borrow my down payment", a: "Some of it. Typically up to half of the equity injection can come from seller financing on standby. The remainder must be your own cash or non-borrowed equity." },
      { q: "How long does franchise SBA financing take", a: "Typically 45-90 days from accepted franchise approval to funding." },
      { q: "What about veteran-owned franchise loans", a: "Qualifying veteran-owned businesses can use SBA Express franchise programs with reduced fees and faster decisions." },
    ],
  },

  "sba-express-loan-for-veterans": {
    whatItIs: [
      "SBA Express for veterans (Veterans Advantage) is a streamlined SBA program offering faster decisions and reduced fees to qualifying veteran-owned small businesses. Loans up to $500K are decided by the SBA-delegated lender, which compresses the timeline meaningfully versus standard 7(a).",
      "For Dallas/Texas veteran owners we confirm eligibility (51%+ veteran-owned, service-disabled veteran, active reservist, or qualifying military spouse), package the file, and route it to an SBA Preferred Lender that actively writes Veterans Advantage.",
    ],
    bestForBusinesses: [
      "Veteran-owned small businesses (51%+ ownership)",
      "Service-disabled veteran-owned small businesses (SDVOSB)",
      "Active reservists and qualifying spouses",
      "Veteran franchisees on the SBA Franchise Directory",
      "Veterans acquiring an existing business",
    ],
    situations: [
      "You qualify as a veteran-owned business under SBA criteria",
      "You want SBA-style terms with a faster timeline",
      "You're financing working capital, equipment, or acquisition under $500K",
      "You'd benefit from reduced SBA guarantee fees",
    ],
    terms: {
      amount: "Up to $500K (Express)",
      term: "Up to 7 years (working capital and revolving)",
      speed: "Decisions in 36 hours from PLP lender",
      qualifications: "51%+ veteran-owned · verifiable eligibility · SBA general 7(a) criteria",
      documentation: "DD-214 or VA documentation · business financials · owner PFS + tax returns",
    },
    commonUses: [
      { h: "Working capital", p: "Up to $500K of flexible working capital for growth, hiring, or expansion." },
      { h: "Equipment", p: "Finance equipment under the Express program with veteran fee discounts." },
      { h: "Small acquisition or buyout", p: "Acquire a small business or buy out a co-owner under Express limits." },
      { h: "Revolving credit line", p: "SBA Express can structure as a revolving line for ongoing cash needs." },
    ],
    faq: [
      { q: "Who qualifies as a veteran-owned business for SBA", a: "Businesses at least 51% owned and controlled by a veteran, service-disabled veteran, active-duty reservist, or qualifying military spouse. We help verify and document eligibility." },
      { q: "What's different about SBA Express for veterans", a: `Reduced (or zero) SBA guarantee fees on most loan amounts, decisions in 36 hours from a Preferred Lender, and a streamlined application, all under the standard SBA 7(a) framework. ${A}` },
      { q: "What's the maximum SBA Express loan amount", a: "$500K. Larger needs route to standard SBA 7(a)." },
      { q: "Can I use SBA Express to acquire a business", a: "Yes, under $500K. Larger acquisitions go to standard 7(a)." },
      { q: "Do I need a DD-214", a: "Yes. Proof of qualifying military service is required. For reservists and spouses, equivalent documentation works." },
    ],
  },

  "women-owned-business-funding": {
    whatItIs: [
      "Women-owned business funding refers to the financing programs and resources available to certified women-owned small businesses (WOSB) and economically disadvantaged women-owned small businesses (EDWOSB). The same SBA, conventional, and revenue-based products are available to everyone, but certification opens additional grant, federal contracting, and lender-targeted programs.",
      "For Dallas-area women-owned businesses we'll structure the right capital stack from the standard programs (SBA, equipment, working capital, line of credit) and point you to certification, grant, and supplier-diversity resources that complement the debt financing.",
    ],
    bestForBusinesses: [
      "Certified WBE / WOSB / EDWOSB businesses",
      "Women-owned businesses considering certification",
      "Women-led companies competing for federal contracts",
      "Established women-owned firms in any industry",
      "Women founders launching with strong owner credit",
    ],
    situations: [
      "You're pursuing federal contracting and need certification",
      "You want lenders that actively report on women-owned lending",
      "You're combining grant funding with traditional debt",
      "You need standard small-business financing and want a knowledgeable advisor",
    ],
    terms: {
      amount: "$5K - $5M+ depending on program",
      term: "Varies by program",
      speed: "Varies · same day to 60+ days",
      qualifications: "51%+ women-owned and controlled · standard credit and revenue criteria apply",
      documentation: "Standard application + business and owner financials",
    },
    commonUses: [
      { h: "Standard small-business financing", p: "Working capital, line of credit, equipment, and SBA, placed with lenders that actively support women-owned businesses." },
      { h: "WOSB certification support", p: "Guidance on SBA WOSB / EDWOSB certification for federal contracting eligibility." },
      { h: "Grant + debt combinations", p: "Pair grant funding with traditional debt to right-size the capital stack." },
      { h: "Supplier-diversity program connections", p: "Connect to corporate supplier-diversity programs for additional revenue opportunity." },
      { h: "SBA Community Advantage", p: "Mission-driven SBA lender programs that prioritize women, minority, and underserved-market borrowers." },
    ],
    faq: [
      { q: "Are there special loans only for women", a: "Loans themselves aren't usually women-specific, but several SBA and Community Advantage lenders explicitly prioritize women-owned files, and federal certifications (WOSB/EDWOSB) open contracting eligibility." },
      { q: "How do I get certified as a women-owned business", a: "WBENC certification is recognized by major corporations; SBA WOSB/EDWOSB certification opens federal set-aside contracts. We can point you to the right path." },
      { q: "Are there grants for women-owned businesses", a: `Yes. Federal, state, and corporate grant programs exist. Grants supplement but rarely replace working debt financing. ${A}` },
      { q: "Are women-owned business loans different from standard SBA", a: "No. They use the same SBA programs. The difference is which lenders actively prioritize and report on women-owned lending." },
      { q: "Do you charge anything to apply", a: `No. ${N}` },
    ],
  },
};

export const getPillarContent = (slug: string | null | undefined) =>
  (slug && PILLAR_CONTENT[slug]) || undefined;
